<template>
  <div class="flexible-weather-tracker">
    <!-- Header -->
    <div class="tracker-header d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-2">
        <div class="header-icon">
          <i class="bi bi-cloud-sun text-white"></i>
        </div>
        <div>
          <h4 class="mb-0">{{ isSharedView ? (sharedViewName || 'Shared View') : trackerDisplayName }}
           
          </h4>
          <div class="d-flex align-items-center gap-2">
            <span v-if="isSharedView" class="badge bg-secondary" style="font-size: 0.7rem;"><i class="bi bi-eye me-1"></i>Read-only shared view</span>
            <template v-else>
              <small v-if="locations?.length" class="text-muted">{{ locations.length }} locations |</small>
              <small class="text-muted">{{ lastExecutedDate ? 'Last run: ' + formatDateTime(lastExecutedDate) : (hasRun ? subtitle : 'Not yet executed') }}</small>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ SHARED VIEW MODE ============ -->
    <template v-if="isSharedView">
      <!-- Shared view: loading -->
      <div v-if="sharedViewLoading" class="text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status" style="width: 2.5rem; height: 2.5rem;">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="text-muted">Loading shared data…</div>
      </div>

      <!-- Shared view: password required -->
      <div v-else-if="sharedViewPasswordRequired" class="text-center py-5">
        <div class="mb-3">
          <i class="bi bi-lock text-muted" style="font-size: 3rem;"></i>
        </div>
        <h5 class="text-muted">Password Required</h5>
        <p class="text-muted small mb-3">This shared view is password-protected. Please enter the password to continue.</p>
        <div class="d-inline-flex align-items-center gap-2" style="max-width: 300px;">
          <input v-model="sharedViewPassword" type="password" class="form-control form-control-sm"
            placeholder="Enter password" @keyup.enter="submitSharedViewPassword" autocomplete="off">
          <button class="btn btn-primary btn-sm" @click="submitSharedViewPassword" :disabled="!sharedViewPassword">
            <i class="bi bi-unlock me-1"></i>Unlock
          </button>
        </div>
        <div v-if="sharedViewError" class="alert alert-danger small mt-3 d-inline-block">{{ sharedViewError }}</div>
      </div>

      <!-- Shared view: no data available -->
      <div v-else-if="sharedViewNoData" class="text-center py-5">
        <div class="mb-3">
          <i class="bi bi-clock-history text-muted" style="font-size: 3rem;"></i>
        </div>
        <h5 class="text-muted">No Data Available Yet</h5>
        <p class="text-muted small mb-3">This tracker has not been executed yet. Please check back later.</p>
      </div>

      <!-- Shared view: error -->
      <div v-else-if="sharedViewError" class="text-center py-5">
        <div class="mb-3">
          <i class="bi bi-exclamation-triangle text-danger" style="font-size: 3rem;"></i>
        </div>
        <h5 class="text-muted">Unable to Load</h5>
        <p class="text-danger small mb-3">{{ sharedViewError }}</p>
        <button class="btn btn-outline-primary btn-sm" @click="loadSharedViewData()">
          <i class="bi bi-arrow-clockwise me-1"></i>Try Again
        </button>
      </div>

      <!-- Shared view: results (full-width, no editor) -->
      <div v-else class="col-12">
        <!-- Shared view: download toolbar -->
        <div class="view-toolbar mb-2">
          <span v-if="hasRun" class="badge bg-light text-dark border" title="Unit group">{{ unitGroup === 'us' ? '°F (US)' : '°C (Metric)' }}</span>
          <div class="ms-auto d-flex align-items-center gap-2">
            <div v-if="hasRun" class="btn-group btn-group-sm" ref="downloadDropdownShared">
              <button class="btn btn-outline-primary" @click="downloadSharedXlsx">
                <i class="bi bi-download me-1"></i>Download
              </button>
              <button type="button" class="btn btn-outline-primary dropdown-toggle dropdown-toggle-split"
                      @click.stop="showDownloadMenu = !showDownloadMenu">
                <span class="visually-hidden">Toggle download options</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end" :class="{ show: showDownloadMenu }">
                <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadSharedXlsx()"><i class="bi bi-file-earmark-spreadsheet me-2"></i>Excel (XLSX)</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadSharedFormat('pdf')"><i class="bi bi-file-earmark-pdf me-2"></i>PDF</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadSharedCsv()"><i class="bi bi-filetype-csv me-2"></i>CSV</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadSharedFormat('json')"><i class="bi bi-filetype-json me-2"></i>JSON</a></li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Shared view: reuse the filter cards -->
        <div v-if="hasRun" class="filter-cards-row">
          <div class="filter-cards-kpis">
            <div class="filter-card" :class="{ 'filter-card-active': filter === 'all' && !criteriaFilter }" @click="setFilterCard('all')">
              <span class="filter-card-count">{{ locationResults.length }}</span>
              <span class="filter-card-label">All</span>
            </div>
            <div class="filter-card filter-card-ok" :class="{ 'filter-card-active': filter === 'ok' && !criteriaFilter }" @click="setFilterCard('ok')">
              <span class="filter-card-count">{{ okLocationCount }}</span>
              <span class="filter-card-label">No Risk</span>
            </div>
            <div class="filter-card filter-card-risk" :class="{ 'filter-card-active': filter === 'flagged' && !criteriaFilter }" @click="setFilterCard('flagged')">
              <span class="filter-card-count">{{ flaggedLocationCount }}</span>
              <span class="filter-card-label">Any Risk</span>
            </div>
           
            <div
              v-for="c in activeCriteriaList" :key="c.id"
              class="filter-card"
              :class="[criteriaCardClass(c), { 'filter-card-active': criteriaFilter === c.id }]"
              :title="c.name || formatCriterionLabel(c)"
              @click="setFilterCard('criteria', c.id)"
            >
              <span class="filter-card-count">{{ criteriaCounts[c.id] || 0 }}</span>
              <span class="filter-card-label">{{ c.name || formatCriterionLabel(c) }}</span>
            </div>
            
          </div>

        </div>

        <!-- Shared view: results panel -->
        <div v-if="hasRun" class="view-panel">
          <div class="view-toggle-bar">
            <div class="btn-group btn-group-sm">
              <button class="btn" :class="viewMode === 'grid' ? 'btn-secondary' : 'btn-outline-secondary'" @click="viewMode = 'grid'">
                <i class="bi bi-grid-3x3 me-1"></i>Grid
              </button>
              <button class="btn" :class="viewMode === 'map' ? 'btn-secondary' : 'btn-outline-secondary'" @click="viewMode = 'map'">
                <i class="bi bi-map me-1"></i>Map
              </button>
              <button class="btn" :class="viewMode === 'list' ? 'btn-secondary' : 'btn-outline-secondary'" @click="viewMode = 'list'">
                <i class="bi bi-shield-exclamation me-1"></i>Risk
              </button>
            </div>
            <div class="filter-card filter-card-search" :class="{ 'filter-card-active': locationSearch.length > 0 }">
              <i class="bi bi-search text-muted" style="font-size: 0.85rem;"></i>
              <input type="text" class="filter-search-input" placeholder="Filter locations…" v-model="locationSearch">
              <button v-if="locationSearch" class="btn btn-link btn-sm p-0 text-muted" @click="locationSearch = ''" title="Clear">
                <i class="bi bi-x-lg" style="font-size: 0.65rem;"></i>
              </button>
            </div>
          </div>

          <div v-if="viewMode === 'grid'" class="results-view">
            <div class="d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
              <div class="d-flex gap-3 align-items-center">
                <div class="btn-group btn-group-sm">
                  <button class="btn" :class="!showHourly ? 'btn-primary' : 'btn-outline-primary'" @click="showHourly = false">
                    <i class="bi bi-calendar-day me-1"></i>Daily
                  </button>
                  <button class="btn" :class="showHourly ? 'btn-primary' : 'btn-outline-primary'" @click="showHourly = true">
                    <i class="bi bi-clock me-1"></i>Hourly
                  </button>
                </div>
                <div class="form-check form-switch mb-0">
                  <input class="form-check-input" type="checkbox" id="sharedExpandAllToggle" :checked="allExpanded" @change="toggleExpandAll">
                  <label class="form-check-label small" for="sharedExpandAllToggle">Expand All</label>
                </div>
              </div>
              <small class="text-muted">Click a location to expand details</small>
            </div>
            <div ref="gridScrollContainer" class="grid-scroll-container" @scroll="onGridScroll">
              <ExceptionDateGrid
                :results="renderedResults"
                :dates="allDates"
                filter="all"
                :unitGroup="unitGroup"
                primaryField="tempmax"
                :showSecondary="true"
                secondaryField="precipprob"
                cornerLabel="Location"
                :locationSort="locationSort"
                @sort-location="cycleLocationSort"
                okLabel="Clear"
                warningLabel="Risk"
                dangerLabel="Risk"
                :binarySeverity="true"
                :expandedLocations="expandedLocationIds"
                :activeCriteria="activeCriteriaList"
                :severityLevels="severityLevels"
                :showHourly="showHourly"
                :businessHours="businessHours"
                :criteriaFilter="criteriaFilter"
                :showLegend="false"
                :ganttMode="true"
                maxHeight="none"
                @cell-click="handleCellClick"
                @toggle-expand="toggleLocationExpand"
              />
              <div v-if="hasMoreResults" class="text-center py-2">
                <small class="text-muted">{{ Math.min(vsStart + vsRenderCount, criteriaFilteredResults.length) }} of {{ criteriaFilteredResults.length }} locations</small>
              </div>
            </div>
          </div>

          <div v-if="viewMode === 'map'" class="results-view">
            <ExceptionMapView
              :results="locationResults"
              :filteredResultIds="mapFilteredIds"
              :criteriaFilterId="criteriaFilter"
              :filterType="filter"
              :criteria="activeCriteriaList"
              :isLoading="false"
              height="500px"
              :dangerLabel="severityLevels?.flag?.label || 'Risk'"
              :showWeatherLayers="true"
              :unitGroup="unitGroup"
              @view-details="showSiteDetails"
            />
          </div>

          <!-- Risk List View -->
          <div v-if="viewMode === 'list'" class="results-view">
            <div v-if="criteriaFilteredResults.length === 0" class="text-center py-5">
              <i class="bi bi-inbox fs-3 text-muted mb-2 d-block"></i>
              <p class="text-muted mb-0">No results to display</p>
            </div>
            <div v-else class="list-group">
              <div
                v-for="result in criteriaFilteredResults" :key="result.id"
                class="list-group-item list-group-item-action"
                @click="showSiteDetails(result)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-1">
                      {{ result.name || result.location }}
                      <i v-if="result.alertsData && result.alertsData.length" class="bi bi-exclamation-triangle-fill text-danger ms-1" style="font-size: 0.85em;" :title="result.alertsData.map(a => a.event).join(', ')"></i>
                    </h6>
                    <small v-if="result.name" class="text-muted">{{ result.address || result.resolvedAddress || result.location }}</small>
                  </div>
                  <div class="text-end">
                    <span v-if="result.status !== 'ok'" class="badge bg-secondary">{{ result.status }}</span>
                    <template v-else-if="getLocationTriggeredCriteria(result).length">
                      <span
                        v-for="c in getLocationTriggeredCriteria(result)" :key="c.id"
                        class="badge rounded-pill me-1" :class="getCriterionBadgeClassByColor(c.color)"
                        style="font-size: 0.7em;"
                      >{{ c.name || formatCriterionLabel(c) }}</span>
                      <div class="small mt-1 text-muted">{{ result.exceptionCount }} day(s)</div>
                    </template>
                    <span v-else class="badge bg-light text-dark border">Clear</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Shared view: powered by -->
        <div class="text-center mt-3">
          <small class="text-muted">
            Powered by <a href="https://www.visualcrossing.com" target="_blank" rel="noopener">Visual Crossing</a>
          </small>
        </div>
      </div>
    </template>

    <!-- ============ NORMAL MODE ============ -->

    <!-- Loading Screen -->
    <div v-if="!isSharedView && initializing" class="initializing-overlay">
      <div class="initializing-content">
        <div class="spinner-border text-primary mb-3" role="status" style="width: 2.5rem; height: 2.5rem;">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="text-muted">Loading tracker data…</div>
      </div>
    </div>

    <!-- Sign-in Required -->
    <div v-else-if="!isSharedView && !hasSession" class="alert alert-warning d-flex align-items-center">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to access weather data.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <!-- Subscription does not include Risk Monitor -->
    <div v-else-if="!isSharedView && !isRiskMonitorAllowed" class="text-center py-5">
      <div class="mb-3">
        <i class="bi bi-shield-lock text-muted" style="font-size: 3rem;"></i>
      </div>
      <h5 class="text-muted">Feature Not Available</h5>
      <p class="text-muted mb-3" style="max-width: 480px; margin: 0 auto;">
        Your current subscription does not include the Multi-Site Weather Risk Monitor.
        Please upgrade your plan to access this feature.
      </p>
      <a class="btn btn-primary" href="https://www.visualcrossing.com/weather-data-editions" target="_blank">
        <i class="bi bi-arrow-up-circle me-2"></i>View Plans
      </a>
    </div>

    <template v-else-if="!isSharedView">
      <div class="row g-4">
        <!-- Left Column: Setup (hidden in shared view) -->
        <div v-show="showEditor && !isSharedView" class="col-lg-4 setup-column">
          <!-- Editor Toolbar: New / Load / Save / Save As / Units -->
          <div class="editor-toolbar mb-2">
            <div class="d-flex flex-nowrap align-items-center gap-1">
              <button class="btn btn-outline-primary btn-sm" @click="newDefinition">
                <i class="bi bi-file-earmark-plus me-1"></i>New
              </button>
              <button class="btn btn-outline-primary btn-sm" @click="openLoadDefinitions()">
                <i class="bi bi-folder2-open me-1"></i>Load
              </button>
              <button class="btn btn-outline-primary btn-sm" :disabled="!canSaveAsBulkTask || isSavingBulkTask" @click="saveOnly">
                <i class="bi bi-floppy me-1"></i>Save
              </button>
              <button class="btn btn-outline-primary btn-sm" :disabled="!canSaveAsBulkTask || isSavingBulkTask" @click="saveAsNew">
                <i class="bi bi-floppy2 me-1"></i>Save&nbsp;As
              </button>
            </div>
          </div>

          <!-- Scrollable settings area -->
          <div class="setup-scroll-area">
          <!-- Locations Panel -->
          <div class="card mb-2">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Locations <span class="badge ms-1" :class="isOverLocationLimit ? 'bg-danger' : 'bg-primary'">{{ locations.length.toLocaleString() }}<template v-if="riskMonitorMaxLocations > 0"> / {{ riskMonitorMaxLocations.toLocaleString() }}</template></span></h6>
              <div class="d-flex gap-1">
                <button class="btn btn-sm btn-outline-primary" @click="$refs.locationPanel.showPasteModal = true" title="Paste from clipboard">
                  <i class="bi bi-clipboard"></i>
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="$refs.locationPanel.showImportModal = true" title="Import from CSV">
                  <i class="bi bi-upload"></i>
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="$refs.locationPanel.showAddForm = !$refs.locationPanel.showAddForm; if ($refs.locationPanel.showAddForm) $nextTick(() => $refs.locationPanel.$refs.addLocationInput?.focus());" title="Add location">
                  <i class="bi bi-plus-lg"></i>
                </button>
                <button v-if="locations.length > 0" class="btn btn-sm btn-outline-danger" @click="showConfirm('clearLocations', `Remove all ${locations.length} locations?`, 'This action cannot be undone.')" title="Clear all">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
            <div class="card-body py-2">
              <ExceptionLocationPanel
                ref="locationPanel"
                v-model="locations"
                label=""
                itemLabel="location"
                icon="bi bi-geo-alt"
                placeholder="Address or coordinates..."
                maxHeight="150px"
                :hideHeader="true"
                :maxLocations="riskMonitorMaxLocations"
              />
            </div>
          </div>

          <!-- Criteria Panel -->
          <div class="card mb-2">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0"><i class="bi bi-funnel me-2"></i>Risk Criteria</h6>
              <div class="d-flex gap-1">
                <button
                  class="btn btn-sm"
                  :class="hasAlertCriterion ? 'btn-danger' : 'btn-outline-danger'"
                  @click="toggleAlertShortcut"
                  title="Toggle Weather Alert monitoring"
                >
                  <i class="bi bi-exclamation-triangle"></i>
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="openCriteriaEditor" title="Edit Criteria">
                  <i class="bi bi-pencil"></i>
                </button>
              </div>
            </div>
            <div class="card-body py-2">
              <!-- Active criteria badges -->
              <div v-if="activeCriteriaList.length === 0" class="text-muted text-center py-1 small">
                No criteria defined. Choose a preset or click edit.
              </div>
              <div v-else class="d-flex flex-wrap gap-1">
                <span
                  v-for="c in activeCriteriaList" :key="c.id"
                  class="badge rounded-pill"
                  :class="getCriterionBadgeClassByColor(c.color)"
                  :title="formatCriterionLabel(c)"
                  style="cursor: pointer;"
                  @click="openCriteriaEditorForCriterion(c.id)"
                >
                  {{ c.name || formatCriterionLabel(c) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Date Range -->
          <div class="card mb-2">
            <div class="card-header py-2">
              <h6 class="mb-0"><i class="bi bi-calendar3 me-2"></i>Date Range</h6>
            </div>
            <div class="card-body py-2">
              <div class="btn-group btn-group-sm w-100 mb-1">
                <button
                  v-for="period in datePeriods" :key="period.value"
                  class="btn"
                  :class="startPeriod === period.value ? 'btn-primary' : 'btn-outline-primary'"
                  @click="changeDateRange(period.value)"
                >{{ period.label }}</button>
              </div>
              <small class="text-muted">
                <i class="bi bi-info-circle me-1"></i>Checking {{ dateRangeDisplay }}
              </small>
            </div>
          </div>

          <!-- Business Hours -->
          <div class="card mb-2">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0"><i class="bi bi-clock me-2"></i>Business Hours</h6>
              <div class="form-check form-switch mb-0">
                <input class="form-check-input" type="checkbox" v-model="businessHours.enabled" id="businessHoursToggle">
              </div>
            </div>
            <div v-if="businessHours.enabled" class="card-body py-2">
              <div class="row g-2">
                <div class="col-6">
                  <label class="form-label small mb-1">Start</label>
                  <input type="time" class="form-control form-control-sm" v-model="businessHours.start">
                </div>
                <div class="col-6">
                  <label class="form-label small mb-1">End</label>
                  <input type="time" class="form-control form-control-sm" v-model="businessHours.end">
                </div>
              </div>
              <small class="text-muted d-block mt-2">
                <i class="bi bi-info-circle me-1"></i>Hourly criteria are only checked during these hours.
              </small>
            </div>
          </div>
          </div><!-- end setup-scroll-area -->

          <!-- Action Buttons (always visible) -->
          <div class="setup-run-button">
          <div class="d-flex gap-2 align-items-center">
            <div class="btn-group btn-group-sm">
              <button class="btn" :class="unitGroup === 'metric' ? 'btn-primary' : 'btn-outline-primary'" @click="changeUnitGroup('metric')">°C</button>
              <button class="btn" :class="unitGroup === 'us' ? 'btn-primary' : 'btn-outline-primary'" @click="changeUnitGroup('us')">°F</button>
            </div>
          <button
            class="btn btn-primary flex-fill"
            :disabled="!canSaveAsBulkTask || silentSaveMode"
            @click="submitOrSilentSave"
          >
            <span v-if="isSavingBulkTask || silentSaveMode">
              <span class="spinner-border spinner-border-sm me-2"></span>
              {{ silentSaveMode && !isSavingBulkTask ? 'Executing...' : 'Queuing...' }}
            </span>
            <span v-else>
              <i class="bi bi-arrow-clockwise me-2"></i>
              {{ localMetadata && localMetadata.id ? 'Refresh data' : 'Save & Submit' }}
            </span>
          </button>
          <button
            v-if="isScheduleAllowed"
            class="btn btn-sm" :class="scheduleRunTimes.length > 0 ? 'btn-outline-success' : 'btn-outline-primary'"
            @click="showScheduleEditor = !showScheduleEditor"
            :title="scheduleRunTimes.length > 0 ? formatScheduleSummary() + ' UTC' : 'Schedule'"
          >
            <i class="bi bi-clock"></i>
          </button>
          </div>
          <div v-if="!lastExecutedDate" class="d-flex gap-2 mt-2">
            <button
              class="btn btn-link flex-fill btn-sm"
              :disabled="!canRun || isRunning"
              @click="doPreview"
            >
              <span v-if="isRunning">
                <span class="spinner-border spinner-border-sm me-2"></span>
                Previewing...
              </span>
              <span v-else>
                <i class="bi bi-eye me-2"></i>
                Preview<span v-if="locations.length > 5" class="ms-1">(first 5 of {{ locations.length }})</span>
              </span>
            </button>

          </div>

          <!-- Schedule Editor -->
          <!-- Schedule Modal -->
          <Teleport to="#vcpopovercontainer">
          <div v-if="showScheduleEditor" class="modal-backdrop fade show"></div>
          <div v-if="showScheduleEditor" class="modal fade show d-block" tabindex="-1" @click.self="showScheduleEditor = false">
            <div class="modal-dialog modal-sm modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header py-2">
                  <h6 class="modal-title mb-0"><i class="bi bi-clock me-1"></i>Schedule (UTC)</h6>
                  <button type="button" class="btn-close btn-close-sm" @click="showScheduleEditor = false"></button>
                </div>
                <div class="modal-body py-2">
                  <div v-for="(time, idx) in scheduleRunTimes" :key="idx"
                    class="input-group input-group-sm mb-1" style="max-width: 220px;">
                    <select class="form-select form-select-sm" :value="getScheduleHour(time)"
                      @change="setScheduleHour(idx, $event.target.value)">
                      <option v-for="hour in 24" :key="hour" :value="hour - 1">
                        {{ String(hour - 1).padStart(2, '0') }}
                      </option>
                    </select>
                    <span class="input-group-text">:</span>
                    <select class="form-select form-select-sm" :value="getScheduleMinute(time)"
                      @change="setScheduleMinute(idx, $event.target.value)">
                      <option v-for="min in [0, 15, 30, 45]" :key="min" :value="min">
                        {{ String(min).padStart(2, '0') }}
                      </option>
                    </select>
                    <button type="button" class="btn btn-outline-danger btn-sm"
                      @click="removeScheduleRunTime(idx)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                  <div v-if="scheduleRunTimes.length === 0" class="text-muted small mb-1">No scheduled times</div>
                  <button type="button" class="btn btn-outline-primary btn-sm"
                    @click="addScheduleRunTime()" :disabled="scheduleRunTimes.length >= 4">
                    <i class="bi bi-plus-circle me-1"></i>Add time
                  </button>
                  <div class="form-check mt-2">
                    <input v-model="scheduleEnabled" class="form-check-input" type="checkbox" id="scheduleEnabled">
                    <label class="form-check-label small" for="scheduleEnabled">Enabled</label>
                  </div>
                </div>
                <div class="modal-footer py-1">
                  <button type="button" class="btn btn-sm btn-primary" @click="showScheduleEditor = false">Done</button>
                </div>
              </div>
            </div>
          </div>
          </Teleport>
          <div v-if="localMetadata && localMetadata.id" class="mt-1 text-center text-muted" style="font-size: 0.7rem;">
            ID: {{ localMetadata.id }}
          </div>
          </div>
        </div>

        <!-- Right Column: Results -->
        <div :class="(showEditor && !isSharedView) ? 'col-lg-8' : 'col-12'">
          <!-- View Toolbar: Editor toggle + Download / Share -->
          <div class="view-toolbar mb-2">
            <button
              v-if="hasRun && !isSharedView"
              class="btn btn-sm btn-outline-primary"
              @click="showEditor = !showEditor"
              :title="showEditor ? 'Hide editor panel' : 'Show editor panel'"
            >
              <i class="bi" :class="showEditor ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'"></i>
              <span class="ms-1 d-none d-md-inline">{{ showEditor ? 'Hide Editor' : 'Show Editor' }}</span>
            </button>
            <span v-if="!showEditor && hasRun" class="badge bg-light text-dark border ms-2" title="Unit group">{{ unitGroup === 'us' ? '°F (US)' : '°C (Metric)' }}</span>
            <div class="ms-auto d-flex align-items-center gap-2">
              <div v-if="canDownloadResult" class="btn-group btn-group-sm" ref="downloadDropdown">
                <button class="btn btn-outline-primary" @click="downloadResultXlsx">
                  <i class="bi bi-download me-1"></i>Download
                </button>
                <button type="button" class="btn btn-outline-primary dropdown-toggle dropdown-toggle-split"
                        @click.stop="showDownloadMenu = !showDownloadMenu">
                  <span class="visually-hidden">Toggle download options</span>
                </button>
                <ul class="dropdown-menu dropdown-menu-end" :class="{ show: showDownloadMenu }">
                  <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultXlsx()"><i class="bi bi-file-earmark-spreadsheet me-2"></i>Excel (XLSX)</a></li>
                  <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultPdf()"><i class="bi bi-file-earmark-pdf me-2"></i>PDF</a></li>
                  <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultCsv()"><i class="bi bi-filetype-csv me-2"></i>CSV</a></li>
                  <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultJson()"><i class="bi bi-filetype-json me-2"></i>JSON</a></li>
                </ul>
              </div>
              <button v-if="canDownloadResult && !isSharedView" class="btn btn-outline-primary btn-sm" @click="shareResultLink" :title="shareCopiedMessage || 'Manage shares'">
                <i class="bi me-1" :class="shareCopiedMessage ? 'bi-check-lg' : 'bi-share'"></i>{{ shareCopiedMessage || 'Share' }}
              </button>
              <button
                v-if="canSaveAsBulkTask && !isSharedView"
                class="btn btn-sm" :class="isDataStale ? 'btn-primary' : 'btn-outline-primary'"
                @click="submitOrSilentSave"
                :disabled="isSavingBulkTask || silentSaveMode"
                title="Refresh data"
              >
                <span v-if="isSavingBulkTask || silentSaveMode">
                  <span class="spinner-border spinner-border-sm me-1"></span>
                </span>
                <span v-else>
                  <i class="bi bi-arrow-clockwise me-1"></i>
                </span>
                Refresh
              </button>
            </div>
          </div>

          <!-- Stale Data Banner -->
          <div v-if="isDataStale && !isRunning" class="stale-banner">
            <i class="bi bi-arrow-repeat"></i>
            <span>Definition changed since last run — results may be outdated.</span>
            <!--
            <button class="btn btn-sm btn-primary ms-auto" @click="submitOrSilentSave" :disabled="!canSaveAsBulkTask || isSavingBulkTask || silentSaveMode">
              <span v-if="isSavingBulkTask || silentSaveMode">
                <span class="spinner-border spinner-border-sm me-1"></span>
                {{ silentSaveMode && !isSavingBulkTask ? 'Executing...' : 'Queuing...' }}
              </span>
              <span v-else>
                <i class="bi bi-arrow-clockwise me-1"></i>Refresh
              </span>
            </button>
            -->
          </div>

          <!-- Progress Bar (when running) -->
          <div v-if="isRunning" class="mb-3">
            <div class="d-flex justify-content-between small mb-1">
              <span>Processing locations...</span>
              <span>{{ completedCount }} / {{ locationResults.length || locations.length }}</span>
            </div>
            <div class="progress" style="height: 6px;">
              <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" :style="{ width: (locations.length ? Math.round(completedCount / locations.length * 100) : 0) + '%' }"></div>
            </div>
          </div>

          <!-- Unified Filter Cards -->
          <div v-if="hasRun" class="filter-cards-row">
            <div class="filter-cards-kpis">
              <div
                class="filter-card" 
                :class="{ 'filter-card-active': filter === 'all' && !criteriaFilter }"
                @click="setFilterCard('all')"
              >
                <span class="filter-card-count">{{ locationResults.length }}</span>
                <span class="filter-card-label">All</span>
              </div>
              <div
                class="filter-card filter-card-ok"
                :class="{ 'filter-card-active': filter === 'ok' && !criteriaFilter }"
                @click="setFilterCard('ok')"
              >
                <span class="filter-card-count">{{ okLocationCount }}</span>
                <span class="filter-card-label">No Risk</span>
              </div>
              <div
                class="filter-card filter-card-risk"
                :class="{ 'filter-card-active': filter === 'flagged' && !criteriaFilter }"
                @click="setFilterCard('flagged')"
              >
                <span class="filter-card-count">{{ flaggedLocationCount }}</span>
                <span class="filter-card-label">Any Risk</span>
              </div>
              
                <div
                  v-for="c in activeCriteriaList" :key="c.id"
                  class="filter-card"
                  :class="[criteriaCardClass(c), { 'filter-card-active': criteriaFilter === c.id }]"
                  :title="c.name || formatCriterionLabel(c)"
                  @click="setFilterCard('criteria', c.id)"
                >
                  <span class="filter-card-count">{{ criteriaCounts[c.id] || 0 }}</span>
                  <span class="filter-card-label">{{ c.name || formatCriterionLabel(c) }}</span>
                </div>
             
            </div>
          </div>

          <!-- View Panel: toggle + content -->
          <div v-if="hasRun && !isRunning" class="view-panel" :class="{ 'view-panel--stale': isDataStale }">
            <!-- View Toggle -->
            <div class="view-toggle-bar">
              <div class="btn-group btn-group-sm">
                <button
                  class="btn"
                  :class="viewMode === 'grid' ? 'btn-secondary' : 'btn-outline-secondary'"
                  @click="viewMode = 'grid'"
                >
                  <i class="bi bi-grid-3x3 me-1"></i>Grid
                </button>
                <button
                  class="btn"
                  :class="viewMode === 'map' ? 'btn-secondary' : 'btn-outline-secondary'"
                  @click="viewMode = 'map'"
                >
                  <i class="bi bi-map me-1"></i>Map
                </button>
                <button
                  class="btn"
                  :class="viewMode === 'list' ? 'btn-secondary' : 'btn-outline-secondary'"
                  @click="viewMode = 'list'"
                >
                  <i class="bi bi-shield-exclamation me-1"></i>Risk
                </button>
              </div>
              <div class="filter-card filter-card-search" :class="{ 'filter-card-active': locationSearch.length > 0 }">
                <i class="bi bi-search text-muted" style="font-size: 0.85rem;"></i>
                <input
                  type="text"
                  class="filter-search-input"
                  placeholder="Filter locations…"
                  v-model="locationSearch"
                >
                <button v-if="locationSearch" class="btn btn-link btn-sm p-0 text-muted" @click="locationSearch = ''" title="Clear">
                  <i class="bi bi-x-lg" style="font-size: 0.65rem;"></i>
                </button>
              </div>
            </div>

          <!-- Grid View -->
          <div v-if="viewMode === 'grid'" class="results-view">
            <!-- Grid Options Bar -->
            <div class="d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
              <div class="d-flex gap-3 align-items-center">
                <div class="btn-group btn-group-sm">
                  <button class="btn" :class="!showHourly ? 'btn-primary' : 'btn-outline-primary'" @click="showHourly = false">
                    <i class="bi bi-calendar-day me-1"></i>Daily
                  </button>
                  <button class="btn" :class="showHourly ? 'btn-primary' : 'btn-outline-primary'" @click="showHourly = true">
                    <i class="bi bi-clock me-1"></i>Hourly
                  </button>
                </div>
                <div class="form-check form-switch mb-0">
                  <input class="form-check-input" type="checkbox" id="expandAllToggle" :checked="allExpanded" @change="toggleExpandAll">
                  <label class="form-check-label small" for="expandAllToggle">Expand All</label>
                </div>
              </div>
              <small class="text-muted">Click a location to expand details</small>
            </div>
            <div ref="gridScrollContainer" class="grid-scroll-container" @scroll="onGridScroll">
                <div v-if="vsTopSpacer > 0" :style="{ height: vsTopSpacer + 'px' }"></div>
                <ExceptionDateGrid
                  :results="renderedResults"
                  :dates="allDates"
                  filter="all"
                  :unitGroup="unitGroup"
                  primaryField="tempmax"
                  :showSecondary="true"
                  secondaryField="precipprob"
                  cornerLabel="Location"
                  :locationSort="locationSort"
                  @sort-location="cycleLocationSort"
                  okLabel="Clear"
                  warningLabel="Risk"
                  dangerLabel="Risk"
                  :binarySeverity="true"
                  :expandedLocations="expandedLocationIds"
                  :activeCriteria="activeCriteriaList"
                  :severityLevels="severityLevels"
                  :showHourly="showHourly"
                  :businessHours="businessHours"
                  :criteriaFilter="criteriaFilter"
                  :showLegend="false"
                  :ganttMode="true"
                  maxHeight="none"
                  @cell-click="handleCellClick"
                  @toggle-expand="toggleLocationExpand"
                />
                <div v-if="hasMoreResults" class="text-center py-2">
                  <small class="text-muted">{{ Math.min(vsStart + vsRenderCount, criteriaFilteredResults.length) }} of {{ criteriaFilteredResults.length }} locations</small>
                </div>
            </div>
          </div>

          <!-- Map View -->
          <div v-if="viewMode === 'map'" class="results-view">
            <ExceptionMapView
              :results="locationResults"
              :filteredResultIds="mapFilteredIds"
              :criteriaFilterId="criteriaFilter"
              :filterType="filter"
              :criteria="activeCriteriaList"
              :isLoading="isRunning"
              height="500px"
              :dangerLabel="severityLevels.flag.label"
              :showWeatherLayers="true"
              :unitGroup="unitGroup"
              @view-details="showSiteDetails"
            />
          </div>

          <!-- List View -->
          <div v-if="viewMode === 'list'" class="results-view">
            <div v-if="criteriaFilteredResults.length === 0" class="text-center py-5">
              <i class="bi bi-inbox fs-3 text-muted mb-2 d-block"></i>
              <p class="text-muted mb-0">No results to display</p>
            </div>
            <div v-else class="list-group">
              <div
                v-for="result in criteriaFilteredResults" :key="result.id"
                class="list-group-item list-group-item-action"
                @click="showSiteDetails(result)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-1">
                      {{ result.name || result.location }}
                      <i v-if="result.alertsData && result.alertsData.length" class="bi bi-exclamation-triangle-fill text-danger ms-1" style="font-size: 0.85em;" :title="result.alertsData.map(a => a.event).join(', ')"></i>
                    </h6>
                    <small v-if="result.name" class="text-muted">{{ result.address || result.resolvedAddress || result.location }}</small>
                  </div>
                  <div class="text-end">
                    <span v-if="result.status !== 'ok'" class="badge bg-secondary">{{ result.status }}</span>
                    <template v-else-if="getLocationTriggeredCriteria(result).length">
                      <span
                        v-for="c in getLocationTriggeredCriteria(result)" :key="c.id"
                        class="badge rounded-pill me-1" :class="getCriterionBadgeClassByColor(c.color)"
                        style="font-size: 0.7em;"
                      >{{ c.name || formatCriterionLabel(c) }}</span>
                      <div class="small mt-1 text-muted">{{ result.exceptionCount }} day(s)</div>
                    </template>
                    <span v-else class="badge bg-light text-dark border">Clear</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Legend -->
          <div class="view-panel-footer">
            <div class="d-flex align-items-center gap-3">
              <small class="text-muted">{{ criteriaFilteredResults.length }} locations</small>
              <div v-if="viewMode === 'grid'" class="d-flex align-items-center gap-2">
                <span class="d-inline-block rounded" style="width:12px;height:12px;background:#fff;border:1px solid #949494;"></span><small class="text-muted">Clear</small>
                <span class="d-inline-block rounded" style="width:12px;height:12px;background:#fff3cd;"></span><small class="text-muted">Risk</small>
                <span class="d-inline-flex align-items-center justify-content-center rounded" style="width:12px;height:12px;"><i class="bi bi-exclamation-triangle-fill text-danger" style="font-size:0.7rem;line-height:1;"></i></span><small class="text-muted">Alert</small>
              </div>
            </div>
          </div>

          </div><!-- /view-panel -->

          <!-- Empty State -->
          <div v-if="!hasRun" class="empty-state text-center py-5">
            <i class="bi bi-sliders fs-1 text-muted mb-3 d-block"></i>
            <h5 class="text-muted">Ready to Analyze</h5>
            <p class="text-muted">
              Add locations, configure criteria, and click "Refresh data" to check conditions.
            </p>
          </div>
        </div>
      </div>
    </template>

    <!-- Criteria Editor Modal -->
    <Teleport to="#vcpopovercontainer">
      <div v-if="showCriteriaModal" class="modal fade show d-block" tabindex="-1">
        <div class="modal-backdrop fade show" @click="showCriteriaModal = false"></div>
        <div class="modal-dialog modal-lg criteria-editor-dialog" style="position: relative; z-index: 1055;">
          <div class="modal-content">
            <div class="modal-header py-2">
              <h6 class="modal-title"><i class="bi bi-sliders me-2"></i>Configure Criteria</h6>
              <button class="btn-close" @click="showCriteriaModal = false"></button>
            </div>
            <div class="modal-body">
              <ExceptionCriteriaEditor
                ref="criteriaEditorRef"
                :modelValue="activeCriteriaList"
                :combineMode="activeRuleset?.combineMode || 'OR'"
                :businessHours="businessHours"
                :unitGroup="unitGroup"
                :showNotification="false"
                :showCancel="true"
                :customPresets="customPresets"
                :focusCriterionId="focusCriterionId"
                @save="onSharedEditorSave"
                @cancel="showCriteriaModal = false"
                @save-preset="onSharedEditorSavePreset"
                @delete-preset="onSharedEditorDeletePreset"
              />
            </div>
            <div class="modal-footer py-2">
              <button class="btn btn-sm btn-light" @click="showCriteriaModal = false">Cancel</button>
              <button class="btn btn-sm btn-primary" @click="$refs.criteriaEditorRef && $refs.criteriaEditorRef.save()">
                <i class="bi bi-check2 me-1"></i>Save Criteria
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Save Definition Modal -->
    <Teleport to="#vcpopovercontainer">
      <div v-if="showSaveDefinitionModal" class="modal fade show d-block" tabindex="-1">
        <div class="modal-backdrop fade show" @click="closeSaveModal"></div>
        <div class="modal-dialog" style="position: relative; z-index: 1055;">
          <div class="modal-content">
            <div class="modal-header py-2">
              <h5 class="modal-title">
                <i class="bi bi-arrow-clockwise me-2"></i>
                {{ pendingRunOnce ? 'Submit Definition' : 'Save Definition' }}
              </h5>
              <button type="button" class="btn-close" @click="closeSaveModal"></button>
            </div>
            <div class="modal-body">

              <!-- Phase 1: Before submission — name / description form -->
              <template v-if="!pollingState.isRefreshingResults && !taskInstance">
                <!-- Error message -->
                <div v-if="saveResultMessage && saveResultIsError" class="alert alert-danger d-flex align-items-center py-2 mb-3">
                  <i class="bi bi-exclamation-triangle-fill me-2"></i>
                  {{ saveResultMessage }}
                </div>
                <!-- Overwrite confirmation -->
                <div v-if="showConfirmOverwrite" class="alert alert-warning py-2 mb-3">
                  A definition named "<strong>{{ saveDefnName.trim() }}</strong>" already exists. Do you want to overwrite it?
                  <div class="mt-2">
                    <button type="button" class="btn btn-sm btn-primary me-2" @click="confirmOverwrite">Yes, Overwrite</button>
                    <button type="button" class="btn btn-sm btn-secondary" @click="cancelOverwrite">Cancel</button>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">Name</label>
                  <input v-model="saveDefnName" type="text" class="form-control" placeholder="My Weather Exceptions" maxlength="120" />
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">Description <span class="text-muted fw-normal">(optional)</span></label>
                  <textarea v-model="saveDefnDesc" class="form-control" rows="2" placeholder="e.g. East coast sites, winter rules" maxlength="500"></textarea>
                </div>
                <div class="text-muted small">
                  <i class="bi bi-info-circle me-1"></i>
                  {{ locations.length }} location{{ locations.length !== 1 ? 's' : '' }},
                  {{ activeCriteriaList.length }} criteria,
                  {{ unitGroup }} units
                </div>
              </template>

              <!-- Phase 2: Polling — waiting for results -->
              <div v-else-if="pollingState.isRefreshingResults" class="text-center py-4">
                <div class="spinner-border text-primary mb-3" role="status" aria-hidden="true"></div>
                <div><strong>{{ processingStatusText }}</strong></div>
                <div class="text-muted small mt-2">Please wait while your data is being processed...</div>
              </div>

              <!-- Phase 3: Error result -->
              <div v-else-if="hasResultError" class="py-3">
                <div class="alert alert-danger d-flex overflow-hidden">
                  <i class="bi bi-exclamation-triangle-fill me-2 flex-shrink-0 mt-1"></i>
                  <div class="text-break" style="min-width: 0;">
                    {{ taskInstance.info || 'An error occurred while processing your request.' }}
                  </div>
                </div>
              </div>

              <!-- Phase 4: Completed successfully -->
              <div v-else-if="hasResult" class="py-3">
                <div class="alert alert-success d-flex align-items-center mb-3">
                  <i class="bi bi-check-circle-fill me-2"></i>
                  <div>
                    <strong>Processing Complete!</strong>
                    <div class="small" v-if="taskInstance.completed && taskInstance.total">
                      {{ taskInstance.completed }} of {{ taskInstance.total }} locations processed.
                    </div>
                  </div>
                </div>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary" @click="loadResultIntoDisplay" :disabled="isLoadingResult">
                    <span v-if="isLoadingResult">
                      <span class="spinner-border spinner-border-sm me-1"></span>Loading...
                    </span>
                    <span v-else>
                      <i class="bi bi-eye me-2"></i>Show Results
                    </span>
                  </button>
                  <div class="btn-group" ref="downloadDropdownModal">
                    <button class="btn btn-outline-primary" @click="downloadResultXlsx">
                      <i class="bi bi-download me-2"></i>Download
                    </button>
                    <button type="button" class="btn btn-outline-primary dropdown-toggle dropdown-toggle-split"
                            @click.stop="showDownloadMenu = !showDownloadMenu">
                      <span class="visually-hidden">Toggle download options</span>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end" :class="{ show: showDownloadMenu }">
                      <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultXlsx()"><i class="bi bi-file-earmark-spreadsheet me-2"></i>Excel (XLSX)</a></li>
                      <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultPdf()"><i class="bi bi-file-earmark-pdf me-2"></i>PDF</a></li>
                      <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultCsv()"><i class="bi bi-filetype-csv me-2"></i>CSV</a></li>
                      <li><a class="dropdown-item" href="#" @click.prevent="showDownloadMenu = false; downloadResultJson()"><i class="bi bi-filetype-json me-2"></i>JSON</a></li>
                    </ul>
                  </div>
                </div>
              </div>

            </div>
            <div class="modal-footer py-2">
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeSaveModal">
                {{ hasResult || hasResultError ? 'Close' : 'Cancel' }}
              </button>
              <button v-if="!pollingState.isRefreshingResults && !taskInstance"
                      type="button" class="btn btn-sm btn-primary"
                      :disabled="!saveDefnName.trim() || isSavingBulkTask" @click="saveAsBulkTask">
                <span v-if="isSavingBulkTask"><span class="spinner-border spinner-border-sm me-1"></span>{{ pendingRunOnce ? 'Submitting...' : 'Saving...' }}</span>
                <span v-else><i class="bi bi-arrow-clockwise me-1"></i>{{ pendingRunOnce ? 'Submit' : 'Save' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Load Definition Modal (lightweight, filtered to weatherexceptions only) -->
    <template v-if="showBulkDefinitionsList">
      <Teleport to="#vcpopovercontainer">
        <div class="modal-backdrop fade show"></div>
        <div class="modal fade show d-block" tabindex="-1">
          <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  <i class="bi bi-folder2-open me-2"></i>
                  Load Saved Definition
                </h5>
                <button type="button" class="btn-close" @click="showBulkDefinitionsList = false"></button>
              </div>
              <div class="modal-body overflow-auto" style="height:500px;">
                <div v-if="isLoadingDefinitions" class="d-flex align-items-center justify-content-center py-5">
                  <div class="spinner-border text-primary" role="status"></div>
                  <span class="ms-2">Loading...</span>
                </div>
                <div v-else-if="!savedDefinitions.length" class="text-muted text-center py-5">
                  No saved weather exception definitions found.
                </div>
                <ul v-else class="list-group">
                  <li
                    v-for="defn in savedDefinitions" :key="defn.id"
                    class="list-group-item"
                  >
                    <!-- Normal row -->
                    <div v-if="pendingDeleteId !== defn.id" class="d-flex justify-content-between align-items-start">
                      <div class="flex-grow-1" role="button" @click="selectSavedDefinition(defn)">
                        <div class="fw-medium">{{ defn.name || 'Untitled' }}</div>
                        <small v-if="defn.description" class="text-muted d-block">{{ defn.description }}</small>
                      </div>
                      <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-3">
                        <div class="text-end">
                          <span v-if="defn.instance && defn.instance.status === 3" class="badge bg-success">{{ defn.instance.total ? defn.instance.total + ' ' : '' }}Results available</span>
                          <span v-else-if="defn.instance && defn.instance.status === 2" class="badge bg-info">Executing</span>
                          <span v-else-if="defn.instance && defn.instance.status === 1" class="badge bg-secondary">Queued</span>
                          <small v-if="defn.results && defn.results[0]" class="text-muted d-block mt-1">{{ formatDateTime(defn.results[0].dateTime) }}</small>
                        </div>
                        <button
                          class="btn btn-sm btn-outline-danger p-1"
                          title="Delete"
                          @click.stop="pendingDeleteId = defn.id"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                    <!-- Inline delete confirmation -->
                    <div v-else class="d-flex justify-content-between align-items-center py-1">
                      <small class="text-danger"><i class="bi bi-exclamation-triangle me-1"></i>Delete "{{ defn.name || 'Untitled' }}"? This cannot be undone.</small>
                      <div class="d-flex gap-1 ms-2 flex-shrink-0">
                        <button class="btn btn-sm btn-danger" @click.stop="confirmDeleteDefinition(defn)">Delete</button>
                        <button class="btn btn-sm btn-outline-secondary" @click.stop="pendingDeleteId = null">Cancel</button>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Teleport>
    </template>

    <!-- Site Detail Modal -->
    <Teleport to="#vcpopovercontainer">
      <div v-if="selectedSite" class="modal fade show d-block" tabindex="-1">
        <div class="modal-backdrop fade show" @click="closeSiteDetail"></div>
        <div class="modal-dialog modal-xl modal-dialog-scrollable site-detail-dialog" style="position: relative; z-index: 1055;">
          <div class="modal-content">
            <div class="modal-header py-2">
              <h5 class="modal-title">
                <i class="bi bi-geo-alt me-2"></i>
                {{ selectedSite.name || selectedSite.location }}
              </h5>
              <button type="button" class="btn-close" @click="closeSiteDetail"></button>
            </div>

            <!-- Tab Navigation -->
            <div class="modal-tab-nav border-bottom px-3">
              <ul class="nav nav-tabs border-0 mb-0">
                <li class="nav-item">
                  <button class="nav-link" :class="{ active: siteDetailTab === 'risks' }" @click="siteDetailTab = 'risks'">
                    <i class="bi bi-shield-exclamation me-1"></i>Risks
                  </button>
                </li>
                <li class="nav-item">
                  <button class="nav-link" :class="{ active: siteDetailTab === 'forecast' }" @click="siteDetailTab = 'forecast'">
                    <i class="bi bi-cloud-sun me-1"></i>Forecast
                  </button>
                </li>
                <li class="nav-item">
                  <button class="nav-link" :class="{ active: siteDetailTab === 'map' }" @click="switchToSiteMap">
                    <i class="bi bi-map me-1"></i>Map
                  </button>
                </li>
              </ul>
            </div>

            <div class="modal-body">

              <!-- ========== TAB 1: RISKS ========== -->
              <div v-if="siteDetailTab === 'risks'">
                <!-- Status Banner -->
                <div class="alert alert-light mb-3 py-2">
                  <div class="d-flex align-items-center flex-wrap gap-1">
                    <i class="bi me-2 fs-5" :class="{
                      'bi-check-circle text-success': selectedSite.maxSeverity === 'ok',
                      'bi-exclamation-triangle text-warning': selectedSite.maxSeverity !== 'ok'
                    }"></i>
                    <template v-if="getLocationTriggeredCriteria(selectedSite).length">
                      <span
                        v-for="c in getLocationTriggeredCriteria(selectedSite)" :key="c.id"
                        class="badge" :class="getCriterionBadgeClassByColor(c.color)"
                      >{{ c.name || formatCriterionLabel(c) }}</span>
                      <span class="small text-muted ms-2">{{ selectedSite.exceptionCount }} day(s) with issues</span>
                    </template>
                    <strong v-else class="text-success">All Clear</strong>
                  </div>
                </div>

                <!-- Active Weather Alerts -->
                <div v-if="selectedSite.alertsData && selectedSite.alertsData.length" class="mb-3">
                  <div class="site-alerts-list">
                    <div
                      v-for="(alert, idx) in selectedSite.alertsData" :key="idx"
                      class="site-alert-card"
                    >
                      <div class="site-alert-header" @click="toggleAlertExpand(idx)">
                        <i class="bi bi-exclamation-diamond-fill site-alert-icon"></i>
                        <span class="site-alert-event">{{ alert.event }}</span>
                        <span v-if="alert.onset || alert.ends" class="site-alert-time">
                          <i class="bi bi-clock me-1"></i>
                          <template v-if="alert.onset">{{ formatDisplayDate(alert.onset) }}</template>
                          <template v-if="alert.onset && alert.ends"> → </template>
                          <template v-if="alert.ends">{{ formatDisplayDate(alert.ends) }}</template>
                        </span>
                        <i class="bi ms-auto site-alert-chevron" :class="expandedAlertIdx === idx ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                      </div>
                      <div v-if="expandedAlertIdx === idx" class="site-alert-body">
                        <div v-if="alert.headline" class="site-alert-headline">{{ alert.headline }}</div>
                        <div v-if="alert.description" class="site-alert-desc" v-html="formatAlertDescription(alert.description)"></div>
                        <a v-if="alert.link" :href="alert.link" target="_blank" rel="noopener" class="site-alert-link">More info <i class="bi bi-box-arrow-up-right"></i></a>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Daily Risk Chart (horizontal) -->
                <div class="mb-3">
                  <h6 class="text-muted mb-2"><i class="bi bi-calendar3 me-1"></i>Daily Risk Overview</h6>
                  <div class="hgantt">
                    <!-- Criterion rows -->
                    <div v-for="c in activeCriteriaList" :key="c.id" class="hgantt-criterion-row">
                      <div class="hgantt-label" :title="c.name || formatCriterionLabel(c)">
                        <span class="daily-gantt-criterion-dot" :style="{ background: getCriterionHexColor(c.color) }"></span>
                        <span>{{ c.name || formatCriterionLabel(c) }}</span>
                      </div>
                      <div class="hgantt-cells hgantt-cells--clickable">
                        <div
                          v-for="day in selectedSite.days" :key="day.datetime"
                          class="hgantt-cell hgantt-cell--daily hgantt-cell--with-value"
                          :class="{
                            'hgantt-cell--flagged': isDayCriterionFlagged(day, c),
                            'hgantt-cell--selected': hourlyExpandedDay === day.datetime,
                            'hgantt-cell--alert': c.field === 'alerts' && isDayInAlertRange(day)
                          }"
                          :style="isDayCriterionFlagged(day, c) ? { background: getCriterionHexColor(c.color) } : {}"
                          :title="formatDayShort(day.datetime) + ' ' + formatDayNum(day.datetime) + ' — ' + (c.name || formatCriterionLabel(c)) + ': ' + getDayCriterionDisplayValue(day, c) + (isDayCriterionFlagged(day, c) ? ' — Flagged' : '')"
                          @click="selectDayForHourly(day)"
                        ><span class="hgantt-cell-text" :class="isDayCriterionFlagged(day, c) ? 'hgantt-cell-text--flagged' : 'hgantt-cell-text--normal'">{{ getDayCriterionDisplayValue(day, c) }}</span></div>
                      </div>
                    </div>
                    <!-- Day labels -->
                    <div class="hgantt-criterion-row hgantt-hour-labels">
                      <div class="hgantt-label"></div>
                      <div class="hgantt-cells">
                        <div
                          v-for="day in selectedSite.days" :key="day.datetime"
                          class="hgantt-cell hgantt-hour-label hgantt-day-label"
                          :class="{ 'hgantt-day-label--selected': hourlyExpandedDay === day.datetime }"
                          @click="selectDayForHourly(day)"
                        ><span class="hgantt-day-name">{{ formatDayShort(day.datetime) }}</span><span class="hgantt-day-num">{{ formatDayNum(day.datetime) }}</span></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Hourly Gantt Chart for Selected Day -->
                <div v-if="hourlyExpandedDay && selectedDayData" class="mb-3">
                  <h6 class="text-muted mb-2">
                    <i class="bi bi-clock me-1"></i>Hourly Risks — {{ formatDisplayDate(hourlyExpandedDay) }}
                  </h6>
                  <div class="hgantt">
                    <!-- Criterion rows -->
                    <div v-for="c in activeCriteriaList" :key="c.id" class="hgantt-criterion-row">
                      <div class="hgantt-label" :title="c.name || formatCriterionLabel(c)">
                        <span class="daily-gantt-criterion-dot" :style="{ background: getCriterionHexColor(c.color) }"></span>
                        <span>{{ c.name || formatCriterionLabel(c) }}</span>
                      </div>
                      <div class="hgantt-cells">
                        <div
                          v-for="hour in selectedDayData.hours" :key="hour.datetime"
                          class="hgantt-cell hgantt-cell--with-value"
                          :class="{
                            'hgantt-cell--flagged': isHourCriterionFlagged(selectedDayData, hour, c),
                            'hgantt-cell--outside': isOutsideBusinessHours(hour),
                            'hgantt-cell--alert': c.field === 'alerts' && isHourCriterionFlagged(selectedDayData, hour, c)
                          }"
                          :style="isHourCriterionFlagged(selectedDayData, hour, c) ? { background: getCriterionHexColor(c.color) } : {}"
                          :title="formatHourLabel(hour.datetime) + ' — ' + (c.name || formatCriterionLabel(c)) + ': ' + getHourCriterionDisplayValue(hour, c)"
                        ><span class="hgantt-cell-text" :class="isHourCriterionFlagged(selectedDayData, hour, c) ? 'hgantt-cell-text--flagged' : 'hgantt-cell-text--normal'">{{ getHourCriterionDisplayValue(hour, c) }}</span></div>
                      </div>
                    </div>
                    <!-- Hour labels -->
                    <div class="hgantt-criterion-row hgantt-hour-labels">
                      <div class="hgantt-label"></div>
                      <div class="hgantt-cells">
                        <div
                          v-for="hour in selectedDayData.hours" :key="hour.datetime"
                          class="hgantt-cell hgantt-hour-label"
                        >{{ formatHourLabel(hour.datetime) }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Selected Day Detail Panel -->
                  <div class="day-detail-panel mt-3">
                    <div class="day-detail-header">
                      <div class="day-detail-date">
                        <span v-if="selectedDayData.icon" class="me-2"><img :src="getWeatherIconUrl(selectedDayData.icon)" :alt="selectedDayData.conditions" class="forecast-wx-icon-sm"></span>
                        <strong>{{ formatDisplayDate(hourlyExpandedDay) }}</strong>
                        <span class="text-muted ms-2">{{ selectedDayData.conditions }}</span>
                      </div>
                      <div class="d-flex gap-3 flex-wrap">
                        <span v-for="c in getSelectedDayMatchedCriteria()" :key="c.id" class="badge" :class="getCriterionBadgeClassByColor(c.color)">{{ c.name || formatCriterionLabel(c) }}</span>
                      </div>
                    </div>
                    <div class="day-detail-grid">
                      <div class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-thermometer-half text-danger"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Temperature</div>
                          <div class="day-detail-stat-value">{{ Math.round(selectedDayData.tempmax || 0) }}° / {{ Math.round(selectedDayData.tempmin || 0) }}°</div>
                          <div v-if="selectedDayData.feelslikemax != null" class="day-detail-stat-sub">Feels {{ Math.round(selectedDayData.feelslikemax) }}° / {{ Math.round(selectedDayData.feelslikemin || 0) }}°</div>
                        </div>
                      </div>
                      <div class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-droplet text-primary"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Precipitation</div>
                          <div class="day-detail-stat-value">{{ Math.round(selectedDayData.precipprob || 0) }}% prob</div>
                          <div class="day-detail-stat-sub">{{ formatValue(selectedDayData.precip, 'precip') }} amount</div>
                        </div>
                      </div>
                      <div class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-wind text-info"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Wind</div>
                          <div class="day-detail-stat-value">{{ formatValue(selectedDayData.windspeed, 'speed') }}</div>
                          <div v-if="selectedDayData.windgust" class="day-detail-stat-sub">Gusts {{ formatValue(selectedDayData.windgust, 'speed') }}</div>
                        </div>
                      </div>
                      <div v-if="selectedDayData.snow > 0 || selectedDayData.snowdepth > 0" class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-snow text-info"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Snow</div>
                          <div class="day-detail-stat-value">{{ formatValue(selectedDayData.snow, 'precip') }}</div>
                          <div v-if="selectedDayData.snowdepth" class="day-detail-stat-sub">Depth {{ formatValue(selectedDayData.snowdepth, 'precip') }}</div>
                        </div>
                      </div>
                      <div class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-moisture text-primary"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Humidity</div>
                          <div class="day-detail-stat-value">{{ Math.round(selectedDayData.humidity || 0) }}%</div>
                        </div>
                      </div>
                      <div v-if="selectedDayData.uvindex != null" class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-sun text-warning"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">UV Index</div>
                          <div class="day-detail-stat-value">{{ selectedDayData.uvindex }}</div>
                          <div class="day-detail-stat-sub">{{ getUVLabel(selectedDayData.uvindex) }}</div>
                        </div>
                      </div>
                      <div v-if="selectedDayData.visibility != null" class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-eye text-secondary"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Visibility</div>
                          <div class="day-detail-stat-value">{{ Math.round(selectedDayData.visibility) }} {{ unitGroup === 'us' ? 'mi' : 'km' }}</div>
                        </div>
                      </div>
                      <div v-if="selectedDayData.pressure != null" class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-speedometer text-secondary"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Pressure</div>
                          <div class="day-detail-stat-value">{{ Math.round(selectedDayData.pressure) }} {{ unitGroup === 'us' ? 'inHg' : 'hPa' }}</div>
                        </div>
                      </div>
                      <div v-if="selectedDayData.sunrise" class="day-detail-stat">
                        <div class="day-detail-stat-icon"><i class="bi bi-sunrise text-warning"></i></div>
                        <div class="day-detail-stat-info">
                          <div class="day-detail-stat-label">Sunrise / Sunset</div>
                          <div class="day-detail-stat-value">{{ formatTimeShort(selectedDayData.sunrise) }} / {{ formatTimeShort(selectedDayData.sunset) }}</div>
                        </div>
                      </div>
                    </div>

                    <!-- Hourly sparkline chart -->
                    <div v-if="selectedDayData.hours && selectedDayData.hours.length" class="day-detail-sparklines mt-3">
                      <h6 class="text-muted mb-2"><i class="bi bi-graph-up me-1"></i>Hourly Trend</h6>
                      <div class="sparkline-container">
                        <canvas ref="sparklineCanvas" class="sparkline-canvas" width="700" height="180"></canvas>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center text-muted py-3">
                  <i class="bi bi-hand-index me-1"></i>Click a day above to see hourly breakdown
                </div>
              </div>

              <!-- ========== TAB 2: FORECAST ========== -->
              <div v-if="siteDetailTab === 'forecast'">
                <div class="site-forecast">
                  <div
                    v-for="day in selectedSite.days" :key="day.datetime"
                    class="forecast-day-card"
                    :class="{ 'forecast-day-card--expanded': forecastExpandedDay === day.datetime }"
                  >
                    <!-- Day summary row -->
                    <div class="forecast-day-header" @click="toggleForecastDay(day)">
                      <div class="forecast-day-date">
                        <div class="forecast-day-name">{{ formatDayShort(day.datetime) }}</div>
                        <div class="forecast-day-num">{{ formatDayNum(day.datetime) }}</div>
                      </div>
                      <div v-if="day.icon" class="forecast-day-icon">
                        <img :src="getWeatherIconUrl(day.icon)" :alt="day.conditions" class="forecast-wx-icon">
                      </div>
                      <div class="forecast-day-temps">
                        <span class="forecast-hi">{{ Math.round(day.tempmax || 0) }}°</span>
                        <span class="forecast-lo text-muted">{{ Math.round(day.tempmin || 0) }}°</span>
                      </div>
                      <div class="forecast-day-precip" :class="{ 'text-primary': (day.precipprob || 0) > 30 }">
                        <i class="bi bi-droplet me-1"></i>{{ Math.round(day.precipprob || 0) }}%
                      </div>
                      <div class="forecast-day-wind">
                        <i class="bi bi-wind me-1"></i>{{ formatValue(day.windspeed, 'speed') }}
                      </div>
                      <div class="forecast-day-cond d-none d-md-block text-muted small text-truncate" style="max-width: 150px;">
                        {{ day.conditions }}
                      </div>
                      <div class="forecast-day-chevron">
                        <i class="bi" :class="forecastExpandedDay === day.datetime ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                      </div>
                    </div>

                    <!-- Expanded hourly forecast -->
                    <div v-if="forecastExpandedDay === day.datetime && day.hours" class="forecast-hourly-panel">
                      <div class="forecast-hourly-grid">
                        <div
                          v-for="hour in day.hours" :key="hour.datetime"
                          class="forecast-hour-row"
                          :class="{ 'forecast-hour-row--outside': isOutsideBusinessHours(hour) }"
                        >
                          <div class="forecast-hour-time">{{ formatHourLabel(hour.datetime) }}</div>
                          <div v-if="hour.icon" class="forecast-hour-icon">
                            <img :src="getWeatherIconUrl(hour.icon)" :alt="hour.conditions" class="forecast-wx-icon-sm">
                          </div>
                          <div class="forecast-hour-temp fw-bold">{{ Math.round(hour.temp || 0) }}°</div>
                          <div class="forecast-hour-precip" :class="{ 'text-primary': (hour.precipprob || 0) > 30 }">
                            <i class="bi bi-droplet"></i> {{ Math.round(hour.precipprob || 0) }}%
                          </div>
                          <div class="forecast-hour-humidity text-muted">
                            <i class="bi bi-moisture"></i> {{ Math.round(hour.humidity || 0) }}%
                          </div>
                          <div class="forecast-hour-wind">
                            <i class="bi bi-wind"></i> {{ formatValue(hour.windspeed, 'speed') }}
                            <span v-if="hour.windgust" class="text-muted small">(g{{ formatValue(hour.windgust, 'speed') }})</span>
                          </div>
                          <div class="forecast-hour-cond text-muted small text-truncate" style="max-width: 120px;">
                            {{ hour.conditions }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ========== TAB 3: MAP ========== -->
              <div v-if="siteDetailTab === 'map'">
                <div class="site-map-container">
                  <ExceptionMapView
                    v-if="siteMapReady"
                    :results="[selectedSite]"
                    :criteria="activeCriteriaList"
                    height="450px"
                    :dangerLabel="severityLevels.flag.label"
                    :showWeatherLayers="true"
                    :unitGroup="unitGroup"
                    :initialCenter="getSiteCenter(selectedSite)"
                    :initialZoom="10"
                    :showViewDetails="false"
                  />
                </div>
              </div>

            </div>
            <div class="modal-footer py-2">
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeSiteDetail">Close</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Styled Confirmation Dialog -->
    <Teleport to="#vcpopovercontainer">
      <div v-if="confirmDialog.show" class="modal fade show d-block" tabindex="-1" style="z-index: 1070;">
        <div class="modal-backdrop fade show" style="z-index: 1065;" @click="confirmDialog.show = false"></div>
        <div class="modal-dialog modal-sm modal-dialog-centered" style="position: relative; z-index: 1075;">
          <div class="modal-content">
            <div class="modal-header py-2">
              <h6 class="modal-title"><i class="bi bi-exclamation-triangle text-warning me-2"></i>Confirm</h6>
              <button class="btn-close" @click="confirmDialog.show = false"></button>
            </div>
            <div class="modal-body">
              <p class="mb-1">{{ confirmDialog.message }}</p>
              <small v-if="confirmDialog.detail" class="text-muted">{{ confirmDialog.detail }}</small>
            </div>
            <div class="modal-footer py-2">
              <button class="btn btn-outline-primary btn-sm" @click="confirmDialog.show = false">Cancel</button>
              <button class="btn btn-danger btn-sm" @click="executeConfirm">
                <i class="bi bi-check-lg me-1"></i>Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Intro Tutorial Modal -->
    <Teleport to="#vcpopovercontainer">
      <template v-if="showTutorialModal && tutorial">
        <div class="modal-backdrop fade show" @click="hideTutorial"></div>
        <div class="modal fade show d-block" tabindex="-1" aria-labelledby="tutorialModalLabel" aria-modal="true" @keydown.esc="hideTutorial">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="tutorialModalLabel">{{ tutorial.title }}</h5>
                <button type="button" class="btn-close" @click="hideTutorial" aria-label="Close"></button>
              </div>
              <div class="modal-body" v-html="tutorial.content"></div>
              <div class="modal-footer">
                <button type="button" class="btn btn-primary" @click="hideTutorial">Got it</button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Teleport>

    <!-- Share Manager Dialog -->
    <ShareManagerDialog
      :show="showShareManager"
      :definitionId="localMetadata?.id"
      :definitionName="localMetadata?.name || trackerDisplayName"
      :serverRoot="serverRoot"
      @close="showShareManager = false"
    />
  </div>
</template>

<script>
import { onMounted } from 'vue';
import { useWeatherExceptionEngine } from '../useWeatherExceptionEngine.js';
import { createCriterion, WEATHER_FIELDS, CRITERIA_OPTIONS, getDefaultColorForField, formatCriterionLabel } from '../exceptionCriteriaPresets.js';
import { ExceptionLocationPanel, ExceptionMapView, ExceptionDateGrid } from '../core';
import { usePackageSave } from '../../queryapp/usePackageSave.js';
import ExceptionCriteriaEditor from '../ExceptionCriteriaEditor.vue';
import ShareManagerDialog from './ShareManagerDialog.vue';
// BulkDefinitionsList replaced with lightweight inline list filtered to weatherexceptions
import { VcJobStatus } from '@/visualcrossing-core/vcjobstatus';
import { VcDateUtils } from '@/visualcrossing-core/vcdateutils';
import { VcLocationsStorage } from '@/visualcrossing-core/vclocationsstorage';

export default {
  name: 'FlexibleWeatherTracker',

  components: {
    ExceptionLocationPanel,
    ExceptionMapView,
    ExceptionDateGrid,
    ExceptionCriteriaEditor,
    ShareManagerDialog,
  },

  props: {
    /** Display title */
    title: { type: String, default: 'Multi-Site Weather Risk Monitor' },
    /** Display subtitle */
    subtitle: { type: String, default: 'Analyze weather conditions across multiple sites' },
    /** Optional override for the API server root URL */
    serverRoot: { type: String, default: null }
  },


  setup(props) {
    const engine = useWeatherExceptionEngine({
      storageKey: 'flexible-tracker',
      defaultStartPeriod: 'next7days',
      defaultUnitGroup: 'us',
      maxConcurrentRequests: 3,
      serverRoot: props.serverRoot
    });

    const { savePackage: savePackageCore } = usePackageSave();

    onMounted(() => {
      engine.initSession();
    });

    // Spread all engine state & methods onto the component instance
    return { ...engine, savePackageCore };
  },

  data() {
    return {
      // Startup gate — true until all local + server state is loaded
      initializing: true,

      // View state
      showEditor: true,
      viewMode: 'grid',

      // Stale data tracking — fingerprint of definition at last run
      lastRunFingerprint: null,

      // Tutorial / intro popup
      showTutorialModal: false,
      tutorial: null,
      tutorials: {
        welcome: {
          id: 'flextracker_welcome',
          title: 'Welcome to Multi-Site Weather Risk Monitor',
          content: '<p>Monitor weather risks across all of your locations at a glance.</p>' +
            '<p><strong>Locations</strong> &mdash; Add the sites you want to track. Paste addresses, import from CSV, or enter them one by one.</p>' +
            '<p><strong>Risk Criteria</strong> &mdash; Define the weather conditions that matter to your operations, such as high wind, extreme heat, or precipitation.</p>' +
            '<p><strong>Run</strong> &mdash; Execute the analysis to see which locations are at risk and drill into the day-by-day detail.</p>' +
            '<p>Your settings are saved automatically so you can return at any time.</p>'
        }
      },
      filter: 'all',
      currentPage: 1,
      pageSize: 25,
      vsStart: 0,
      vsTopSpacer: 0,
      vsHeightCache: {},
      vsRenderCount: 80,
      selectedSite: null,
      siteDetailTab: 'risks',
      forecastExpandedDay: null,
      siteMapReady: false,
      expandedLocationIds: [],
      showHourly: false,
      criteriaFilter: null,
      locationSearch: '',
      locationSort: 'none',
      hourlyExpandedDay: null,
      expandedAlertIdx: -1,
      expandAllSetting: false,

      // Download dropdown state
      showDownloadMenu: false, // legacy – kept for compat but unused (Bootstrap handles dropdown state)

      // Modal state
      showCriteriaModal: false,
      focusCriterionId: null,
      editingCriteria: [],
      newPresetName: '',
      criteriaEditorError: '',

      // Confirmation dialog state
      confirmDialog: {
        show: false,
        action: null,
        message: '',
        detail: ''
      },

      // Severity level customization
      severityLevels: {
        ok: { label: 'Clear', color: '#d1e7dd', textColor: '#0a3622', badgeClass: 'bg-success' },
        caution: { label: 'Caution', color: '#fff3cd', textColor: '#664d03', badgeClass: 'bg-warning text-dark' },
        flag: { label: 'Threshold', color: '#f8d7da', textColor: '#58151c', badgeClass: 'bg-danger' }
      },

      // Business hours configuration
      businessHours: {
        enabled: false,
        start: '08:00',
        end: '17:00'
      },

      // Bulk task save/load state
      isSavingBulkTask: false,
      isLoadingResult: false,
      shareCopiedMessage: null,
      showShareManager: false,

      // Shared view mode (read-only, ?share= URL param)
      isSharedView: false,
      shareToken: null,
      sharedViewLoading: false,
      sharedViewError: null,
      sharedViewName: null,
      sharedViewPasswordRequired: false,
      sharedViewPassword: '',
      sharedViewNoData: false,
      showSaveDefinitionModal: false,
      saveDefnName: '',
      saveDefnDesc: '',
      saveResultMessage: null,
      saveResultIsError: false,
      showBulkDefinitionsList: false,
      isLoadingDefinitions: false,
      savedDefinitions: [],
      pendingDeleteId: null,
      // Overwrite confirmation state
      showConfirmOverwrite: false,
      existingPackageToOverwrite: null,
      overwriteResolve: null,
      // Schedule state
      scheduleRunTimes: [],
      scheduleEnabled: true,
      showScheduleEditor: false,

      // Polling state (after submit)
      localMetadata: null,
      taskInstance: null,
      lastExecutedDate: null,
      silentSaveMode: false,
      pendingRunOnce: true,
      pollingState: {
        isPolling: false,
        pollingRetries: 0,
        hasExceedRetries: false,
        isRefreshingResults: false,
        pollingTimer: null
      },

      // Date period options
      datePeriods: [
        { value: 'next3days', label: '3 Days' },
        { value: 'next7days', label: '7 Days' },
        { value: 'next10days', label: '10 Days' },
        { value: 'next14days', label: '14 Days' }
      ],

      // Built-in presets
      presets: [
        {
          id: 'outdoor-event',
          name: 'Outdoor Event',
          icon: 'bi bi-calendar-event',
          description: 'No rain, mild temps, light wind',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'tempmin', op: 'lt', metricValue: 7, usValue: 45, name: 'Too cold', color: 'teal' },
            { field: 'tempmax', op: 'gt', metricValue: 35, usValue: 95, name: 'Too hot', color: 'danger' },
            { field: 'windgust', op: 'gt', metricValue: 40, usValue: 25, name: 'High wind', color: 'purple' }
          ]
        },
        {
          id: 'construction',
          name: 'Construction Work',
          icon: 'bi bi-cone-striped',
          description: 'Safe conditions for outdoor work',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'tempmin', op: 'lt', metricValue: 2, usValue: 35, name: 'Freezing risk', color: 'teal' },
            { field: 'windgust', op: 'gt', metricValue: 50, usValue: 30, name: 'Dangerous wind', color: 'purple' },
            { field: 'snow', op: 'gt', value: 0, name: 'Snow expected', color: 'cyan' }
          ]
        },
        {
          id: 'agriculture',
          name: 'Farming / Agriculture',
          icon: 'bi bi-flower1',
          description: 'Planting and harvesting conditions',
          rules: [
            { field: 'tempmin', op: 'lt', metricValue: 2, usValue: 35, name: 'Frost risk', color: 'teal' },
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'windgust', op: 'gt', metricValue: 40, usValue: 25, name: 'High wind', color: 'purple' }
          ]
        },
        {
          id: 'aviation',
          name: 'Aviation / Flying',
          icon: 'bi bi-airplane',
          description: 'VFR flight conditions',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Precipitation likely', color: 'info' },
            { field: 'windgust', op: 'gt', metricValue: 50, usValue: 30, name: 'High wind', color: 'purple' },
            { field: 'snow', op: 'gt', value: 0, name: 'Snow/ice risk', color: 'cyan' },
            { field: 'severerisk', op: 'gt', value: 30, name: 'Severe weather risk', color: 'danger' }
          ]
        },
        {
          id: 'outdoor-leisure',
          name: 'Outdoor Leisure',
          icon: 'bi bi-tree',
          description: 'Hiking, sports, and outdoor activities',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'tempmax', op: 'gt', metricValue: 37, usValue: 99, name: 'Extreme heat', color: 'danger' },
            { field: 'tempmin', op: 'lt', metricValue: -5, usValue: 23, name: 'Very cold', color: 'teal' },
            { field: 'windgust', op: 'gt', metricValue: 50, usValue: 30, name: 'High wind', color: 'purple' },
            { field: 'severerisk', op: 'gt', value: 30, name: 'Severe weather', color: 'danger' }
          ]
        },
        {
          id: 'shipping',
          name: 'Temperature-Sensitive Shipping',
          icon: 'bi bi-truck',
          description: 'Safe conditions for perishable goods',
          rules: [
            { field: 'tempmax', op: 'gt', metricValue: 30, usValue: 86, name: 'Too hot for cargo', color: 'danger' },
            { field: 'tempmin', op: 'lt', metricValue: 2, usValue: 35, name: 'Freezing risk', color: 'teal' },
            { field: 'severerisk', op: 'gt', value: 30, name: 'Severe weather risk', color: 'danger' }
          ]
        }
      ],

      // Custom presets saved by user
      customPresets: []
    };
  },

  computed: {
    /** Canonical JSON snapshot of the full definition (mirrors the structure submitted on save/run) */
    currentDefinitionFingerprint() {
      return JSON.stringify(this.buildDefinitionSnapshot());
    },

    /** Whether the displayed results are stale (definition changed since last run) */
    isDataStale() {
      return this.hasRun && this.lastRunFingerprint !== null && this.lastRunFingerprint !== this.currentDefinitionFingerprint;
    },

    /** Display name: saved definition name or 'new tracker' */
    trackerDisplayName() {
      if (this.localMetadata && this.localMetadata.name) return this.localMetadata.name;
      return 'New tracker';
    },

    /** The selected day's data object for hourly display */
    selectedDayData() {
      if (!this.selectedSite?.days || !this.hourlyExpandedDay) return null;
      return this.selectedSite.days.find(d => d.datetime === this.hourlyExpandedDay) || null;
    },

    /** Grouped options for the criteria field dropdown */
    criteriaOptionGroups() {
      const groupMap = {};
      for (const opt of CRITERIA_OPTIONS) {
        if (!groupMap[opt.group]) groupMap[opt.group] = { label: opt.group, options: [] };
        groupMap[opt.group].options.push(opt);
      }
      return Object.values(groupMap);
    },

    /** Active criteria from the engine's ruleset */
    activeCriteriaList() {
      return this.activeRuleset?.criteria || [];
    },

    /** Human-readable date range description */
    dateRangeDisplay() {
      const period = this.datePeriods.find(p => p.value === this.startPeriod);
      return period ? period.label.toLowerCase() : 'selected period';
    },

    /** Maximum number of locations allowed by the riskmonitor subscription param */
    riskMonitorMaxLocations() {
      const params = this.sessionState?.subscriptionParams;
      if (!params) return 0;
      const val = params['riskmonitor.maxLocations'];
      return typeof val === 'number' ? val : parseInt(val, 10) || 0;
    },

    /** Whether the user's subscription allows use of the risk monitor */
    isRiskMonitorAllowed() {
      return this.riskMonitorMaxLocations > 0;
    },

    /** Whether the current location count exceeds the subscription limit */
    isOverLocationLimit() {
      return this.riskMonitorMaxLocations > 0 && this.locations.length > this.riskMonitorMaxLocations;
    },

    /** Whether the user's subscription allows scheduling */
    isScheduleAllowed() {
      const params = this.sessionState?.subscriptionParams;
      return params?.bulkAllowSubscriptions === 1;
    },

    /** Whether the current configuration can be saved as a bulk task */
    canSaveAsBulkTask() {
      return this.hasSession && this.locations.length > 0 && this.activeCriteriaList.length > 0 && !this.isOverLocationLimit;
    },

    /** Polling status text shown while waiting for results */
    processingStatusText() {
      if (!this.taskInstance) return 'Queuing...';
      if (this.taskInstance.status === 1) return 'Queued — waiting to execute...';
      if (this.taskInstance.status === 2) {
        if (this.taskInstance.completed > 1 && this.taskInstance.total > 1) {
          return `Executing ${this.taskInstance.completed} of ${this.taskInstance.total}`;
        }
        return 'Executing...';
      }
      return 'Processing...';
    },

    /** Whether the submitted task has completed successfully */
    hasResult() {
      return this.taskInstance && this.taskInstance.status === 3;
    },

    /** Whether the submitted task has failed */
    hasResultError() {
      return this.taskInstance && this.taskInstance.status === 4;
    },

    /** Whether download buttons should be available (results exist on server) */
    canDownloadResult() {
      return this.localMetadata && (this.hasResult || this.locationResults.length > 0);
    },

    /** Whether all locations are currently expanded */
    allExpanded() {
      return this.locationResults.length > 0 && this.expandedLocationIds.length >= this.locationResults.length;
    },

    /** Count of locations with no risk */
    okLocationCount() {
      return this.locationResults.filter(r => r.status === 'ok' && r.maxSeverity === 'ok').length;
    },

    /** Count of locations with any risk */
    flaggedLocationCount() {
      return this.locationResults.filter(r => r.status === 'ok' && (r.maxSeverity === 'warning' || r.maxSeverity === 'danger')).length;
    },

    /** Per-criterion location counts */
    criteriaCounts() {
      const counts = {};
      for (const c of this.activeCriteriaList) {
        counts[c.id] = this.locationResults.filter(r => r.triggeredCriteria?.includes(c.id)).length;
      }
      return counts;
    },

    /** Results filtered by severity */
    filteredResults() {
      if (this.filter === 'all') return this.locationResults;
      return this.locationResults.filter(r => {
        if (this.filter === 'ok') return r.maxSeverity === 'ok';
        if (this.filter === 'flagged') return r.maxSeverity === 'warning' || r.maxSeverity === 'danger';
        return true;
      });
    },

    /** Array of location strings that pass the current filter — null when no filter is active */
    mapFilteredIds() {
      const hasFilter = this.filter !== 'all' || !!this.criteriaFilter || !!this.locationSearch.trim();
      if (!hasFilter) return null;
      return this.criteriaFilteredResults.map(r => r.location);
    },

    /** Results filtered by severity, then by criteria AND location search — used by map & list */
    criteriaFilteredResults() {
      let results = this.filteredResults;
      const hasCriteria = !!this.criteriaFilter;
      const hasSearch = !!this.locationSearch.trim();
      if (hasCriteria || hasSearch) {
        const q = hasSearch ? this.locationSearch.trim().toLowerCase() : '';
        results = results.filter(r => {
          if (hasCriteria && !r.triggeredCriteria?.includes(this.criteriaFilter)) return false;
          if (hasSearch) {
            const matchesSearch = (r.location || '').toLowerCase().includes(q) ||
              (r.name || '').toLowerCase().includes(q) ||
              (r.resolvedAddress || '').toLowerCase().includes(q);
            if (!matchesSearch) return false;
          }
          return true;
        });
      }
      // Apply location sort
      if (this.locationSort !== 'none') {
        const dir = this.locationSort === 'asc' ? 1 : -1;
        results = [...results].sort((a, b) => {
          const nameA = (a.name || a.location || '').toLowerCase();
          const nameB = (b.name || b.location || '').toLowerCase();
          return nameA < nameB ? -dir : nameA > nameB ? dir : 0;
        });
      }
      return results;
    },

    /** Max items to keep in DOM before trimming from top */
    vsMaxItems() {
      return this.showHourly ? 50 : 150;
    },

    /** The slice of results actually rendered in the DOM */
    renderedResults() {
      const end = Math.min(this.criteriaFilteredResults.length, this.vsStart + this.vsRenderCount);
      return this.criteriaFilteredResults.slice(this.vsStart, end);
    },

    /** Whether there are more results beyond the current window */
    hasMoreResults() {
      return (this.vsStart + this.vsRenderCount) < this.criteriaFilteredResults.length;
    },

    /** Whether a Weather Alert criterion is currently active */
    hasAlertCriterion() {
      return this.activeCriteriaList.some(c => c.field === 'alerts' && c.operator === 'present');
    },

    /** Combined built-in + custom presets */
    allPresets() {
      return [...this.presets, ...this.customPresets];
    }
  },

  watch: {
    filter() { this.vsReset(); },
    criteriaFilter() { this.vsReset(); },
    locationResults() { this.vsReset(); },
    showHourly() { this.vsReset(); },
    activeCriteriaList(list) {
      if (this.criteriaFilter && !list.some(c => c.id === this.criteriaFilter)) {
        this.criteriaFilter = null;
      }
    },
    /** Auto-select first exception day when opening site detail, draw sparklines on day change */
    selectedSite(site) {
      if (site?.days?.length) {
        const firstException = site.days.find(d => d.isException);
        if (firstException) {
          this.hourlyExpandedDay = firstException.datetime;
        } else {
          this.hourlyExpandedDay = site.days[0].datetime;
        }
      }
    },
    hourlyExpandedDay() {
      this.$nextTick(() => this.drawSparklines());
    },
    locations: {
      handler(newVal) {
        if (!this._suppressPersist) this.persistLocations(newVal);
      },
      deep: true
    },
    businessHours: {
      handler() {
        if (!this._suppressPersist) this.persistBusinessHours();
        this.setBusinessHours(this.businessHours);
      },
      deep: true
    },
    viewMode(val) {
      if (!this._suppressPersist) {
        try { localStorage.setItem('flexTracker_viewMode', val); } catch (e) { /* ignore */ }
      }
    },
    showHourly(val) {
      if (!this._suppressPersist) {
        try { localStorage.setItem('flexTracker_showHourly', String(val)); } catch (e) { /* ignore */ }
      }
    },
    /** When session becomes available during startup, reload persisted definition from server */
    sessionId(newId, oldId) {
      // Only handle initial session arrival during startup.
      // User switches are handled by the createsession event handler.
      if (newId && !oldId && this.localMetadata && this.localMetadata.id && !this._defnLoadedFromServer) {
        if (this._initTimeout) { clearTimeout(this._initTimeout); this._initTimeout = null; }
        this.initializing = true;
        this.loadPersistedMetadata().catch(() => {}).finally(() => {
          this.initializing = false;
          this.showTutorialIntro('welcome', true);
        });
      }
    }
  },

  async mounted() {
    // Close download dropdown on outside click (register early so
    // it covers both shared and normal views)
    this._closeDownloadMenu = (e) => {
      const refs = [this.$refs.downloadDropdown, this.$refs.downloadDropdownShared, this.$refs.downloadDropdownModal];
      const inside = refs.some(dd => dd && dd.contains && dd.contains(e.target));
      if (!inside) {
        this.showDownloadMenu = false;
      }
    };
    document.addEventListener('click', this._closeDownloadMenu);

    // Check for shared view mode (?share= URL param)
    const urlParams = new URLSearchParams(window.location.search);
    const shareToken = urlParams.get('share');
    if (shareToken) {
      this.isSharedView = true;
      this.shareToken = shareToken;
      this.showEditor = false;
      this.initializing = false;
      await this.loadSharedViewData();
      return;
    }

    // Suppress watcher-driven persistence while restoring saved state
    this._suppressPersist = true;

    // 1. Restore all local state synchronously (no flicker from watchers)
    this.loadPersistedLocations();
    this.loadPersistedCriteria();
    this.loadCustomPresets();
    this.loadPersistedBusinessHours();
    this.loadPersistedSeverityLevels();
    this.loadViewSettings();
    this.setBusinessHours(this.businessHours);

    // 2. Re-enable persistence now that local state is loaded
    this._suppressPersist = false;

    // 3. Attempt to reload server-side definition + results (async)
    //    The loading screen stays visible until this completes.
    try {
      await this.loadPersistedMetadata();
    } catch (e) {
      console.warn('Startup: failed to load persisted definition', e);
    }

    // 4. Reveal the UI — unless we have a saved definition that couldn't
    //    be loaded yet because the session hasn't arrived.  In that case
    //    the sessionId watcher will clear `initializing` once it finishes.
    if (this._defnLoadedFromServer || !this.localMetadata?.id || !window.vcCommunicator) {
      this.initializing = false;
    } else {
      // Safety timeout: don't leave the loading screen up forever
      this._initTimeout = setTimeout(() => {
        if (this.initializing) {
          this.initializing = false;
          this.showTutorialIntro('welcome', true);
        }
      }, 5000);
    }

    // Show intro tutorial on first visit
    if (!this.initializing) this.showTutorialIntro('welcome', true);

    // Listen for session creation in case user signs in after load
    const vc = window.vcCommunicator;
    if (vc) {
      vc.addHandler('terminatesession', () => {
        if (this._isDestroyed) return;
        this.resetForSessionChange();
      });

      vc.addHandler('createsession', () => {
        if (this._isDestroyed) return;
        // Clear any stale state from a previous user
        this.resetForSessionChange();
        // Attempt to load the new user's persisted definition
        this.initializing = true;
        this.loadPersistedMetadata().catch(() => {}).finally(() => {
          this.initializing = false;
          this.showTutorialIntro('welcome', true);
        });
      });
    }
  },

  beforeUnmount() {
    this._isDestroyed = true;

    if (this._initTimeout) { clearTimeout(this._initTimeout); this._initTimeout = null; }
    if (this._vsRafId) {
      cancelAnimationFrame(this._vsRafId);
      this._vsRafId = null;
    }
    if (this.pollingState?.pollingTimer) {
      clearTimeout(this.pollingState.pollingTimer);
      this.pollingState.pollingTimer = null;
    }
    this.pollingState.isRefreshingResults = false;
    this.pollingState.isPolling = false;

    if (this._closeDownloadMenu) {
      document.removeEventListener('click', this._closeDownloadMenu);
    }
  },

  methods: {
    // ============================================
    // Criteria Editor
    // ============================================

    /** Cycle location sort: none → asc → desc → none */
    cycleLocationSort() {
      if (this.locationSort === 'none') this.locationSort = 'asc';
      else if (this.locationSort === 'asc') this.locationSort = 'desc';
      else this.locationSort = 'none';
    },

    /** Show a dismissable tutorial popup (same pattern as QueryApp) */
    showTutorialIntro(id, dismissable) {
      if (dismissable && VcLocationsStorage.getValue('tutorial_' + id)) return;
      this.tutorial = this.tutorials[id];
      if (!this.tutorial) return;
      this._tutorialKey = id;
      this.showTutorialModal = true;
    },

    /** Hide the tutorial modal and remember dismissal */
    hideTutorial() {
      this.showTutorialModal = false;
      if (this._tutorialKey) {
        VcLocationsStorage.setValue('tutorial_' + this._tutorialKey, 1);
      }
      this.tutorial = null;
      this._tutorialKey = null;
    },

    /** Open the criteria editor modal */
    openCriteriaEditor() {
      this.focusCriterionId = null;
      this.showCriteriaModal = true;
    },

    /** Open criteria editor and focus on a specific criterion */
    openCriteriaEditorForCriterion(criterionId) {
      this.focusCriterionId = criterionId;
      this.showCriteriaModal = true;
    },

    /** Handle save from the shared ExceptionCriteriaEditor component */
    onSharedEditorSave(payload) {
      this.setCriteria(payload.criteria, payload.combineMode || 'OR', { skipInvalidation: this.hasRun });
      // Sync business hours from editor
      if (payload.businessHours) {
        this.businessHours.enabled = !!payload.businessHours.enabled;
        this.businessHours.start = payload.businessHours.start || '08:00';
        this.businessHours.end = payload.businessHours.end || '17:00';
        this.setBusinessHours(this.businessHours);
        this.persistBusinessHours();
      }
      this.persistCriteria();
      this.showCriteriaModal = false;
    },

    /** Handle save-preset from the shared ExceptionCriteriaEditor component */
    onSharedEditorSavePreset(preset) {
      const existingIdx = this.customPresets.findIndex(p => p.name === preset.name);
      if (existingIdx >= 0) {
        this.customPresets.splice(existingIdx, 1, preset);
      } else {
        this.customPresets.push(preset);
      }
      this.saveCustomPresets();
    },

    /** Handle delete-preset from the shared ExceptionCriteriaEditor component */
    onSharedEditorDeletePreset(preset) {
      const idx = this.customPresets.findIndex(p => p.id === preset.id);
      if (idx >= 0) {
        this.customPresets.splice(idx, 1);
        this.saveCustomPresets();
      }
    },

    loadCustomPresets() {
      try {
        const saved = localStorage.getItem('flexTracker_customPresets');
        if (saved) this.customPresets = JSON.parse(saved);
      } catch (e) { /* ignore */ }
    },

    saveCustomPresets() {
      try {
        localStorage.setItem('flexTracker_customPresets', JSON.stringify(this.customPresets));
      } catch (e) { /* ignore */ }
    },

    // ============================================
    // Persistence (locations + criteria)
    // ============================================

    persistMetadata() {
      try {
        if (this.localMetadata && this.localMetadata.id) {
          localStorage.setItem('flexTracker_metadata', JSON.stringify({
            id: this.localMetadata.id,
            name: this.localMetadata.name || '',
            desc: this.localMetadata.desc || ''
          }));
        } else {
          localStorage.removeItem('flexTracker_metadata');
        }
      } catch (e) { /* ignore */ }
    },

    /** Restore persisted metadata and reload the definition from server if possible */
    async loadPersistedMetadata() {
      try {
        const saved = localStorage.getItem('flexTracker_metadata');
        if (!saved) return;
        const meta = JSON.parse(saved);
        if (!meta || !meta.id) return;
        // We have a saved definition ID — try to reload it from the server
        const vc = window.vcCommunicator;
        const sid = vc?.sessionId || this.sessionId;
        if (!vc || !sid) {
          // Not signed in yet — just restore the metadata linkage so that
          // if they sign in later the ID is still remembered
          this.localMetadata = meta;
          if (meta.name) this.saveDefnName = meta.name;
          if (meta.desc) this.saveDefnDesc = meta.desc;
          return;
        }
        const taskDefn = await vc.loadBulkSourceDefn(meta.id);
        if (taskDefn) {
          this._defnLoadedFromServer = true;
          this.onLoadDefinition(meta, taskDefn);

          // Also fetch instance/results so we can display completed results
          try {
            const packageResults = await vc.resultsByJobPackage(meta.id);
            if (packageResults && packageResults[0] && packageResults[0].instance) {
              this.taskInstance = packageResults[0].instance;
              if (packageResults[0].instance?.dateTime) this.lastExecutedDate = packageResults[0].instance.dateTime;
              if (VcJobStatus.isFinishedState(this.taskInstance.status)) {
                const loaded = await this.loadResultIntoDisplay();
                if (loaded) {
                  this.showEditor = false;
                }
              }
            }
          } catch (resErr) {
            console.warn('Failed to load results for persisted definition:', resErr);
            this.showEditor = true;
          }
        } else {
          // Definition no longer exists on server — clear stale reference
          localStorage.removeItem('flexTracker_metadata');
          this.localMetadata = null;
        }
      } catch (e) {
        console.warn('Failed to reload persisted definition:', e);
        this.showEditor = true;
      }
    },

    persistLocations(locs) {
      try {
        localStorage.setItem('flexTracker_locations', JSON.stringify(locs));
      } catch (e) { /* ignore */ }
    },

    loadPersistedLocations() {
      try {
        const saved = localStorage.getItem('flexTracker_locations');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.setLocations(parsed);
          }
        }
      } catch (e) { /* ignore */ }
    },

    persistCriteria() {
      try {
        const criteria = (this.activeRuleset?.criteria || []).map(c => ({
          field: c.field, operator: c.operator, value: c.value,
          granularity: c.granularity, name: c.name, color: c.color, _quickId: c._quickId
        }));
        localStorage.setItem('flexTracker_criteria', JSON.stringify(criteria));
      } catch (e) { /* ignore */ }
    },

    loadPersistedCriteria() {
      try {
        const saved = localStorage.getItem('flexTracker_criteria');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const criteria = parsed.map(c => {
              // Support legacy 'section' property from old saved data
              const granularity = c.granularity || (c.section === 'hours' ? 'hourly' : 'daily');
              const criterion = createCriterion(
                c.field, c.operator, c.value, granularity, c.name, { color: c.color }
              );
              if (c._quickId) criterion._quickId = c._quickId;
              return criterion;
            });
            this.setCriteria(criteria, 'OR');
          }
        }
      } catch (e) { /* ignore */ }
    },

    // ============================================
    // Display Helpers
    // ============================================

    /** Get severity label by severity key */
    getSeverityLabel(severity) {
      if (severity === 'ok') return 'Clear';
      return 'Risk';
    },

    /** Status label for a location result */
    getStatusLabel(result) {
      if (result.status !== 'ok') return 'Error';
      return result.maxSeverity === 'ok' ? 'Clear' : 'Risk';
    },

    /** Scroll handler — grow-and-trim virtual scroll */
    onGridScroll(e) {
      if (this._vsRafId) return;
      const el = e.target;
      this._vsRafId = requestAnimationFrame(() => {
        this._vsRafId = null;
        this._vsHandleScroll(el);
      });
    },

    _vsHandleScroll(el) {
      const total = this.criteriaFilteredResults.length;
      const growBatch = this.showHourly ? 10 : 30;
      const trimBatch = this.showHourly ? 10 : 30;
      const scrollTop = el.scrollTop;
      const scrollBottom = scrollTop + el.clientHeight;
      const scrollHeight = el.scrollHeight;
      const currentEnd = Math.min(total, this.vsStart + this.vsRenderCount);

      // === GROW: near bottom, append more items ===
      if (scrollBottom >= scrollHeight - 400 && currentEnd < total) {
        this.vsRenderCount += Math.min(growBatch, total - currentEnd);
      }

      // === TRIM: too many items in DOM, remove from top ===
      if (this.vsRenderCount > this.vsMaxItems) {
        const contentAboveView = scrollTop - this.vsTopSpacer;
        if (contentAboveView > 600) {
          this._vsMeasure(el);
          let trimHeight = 0;
          let trimCount = 0;
          for (let i = this.vsStart; i < this.vsStart + trimBatch; i++) {
            const h = this.vsHeightCache[i] || 50;
            if (trimHeight + h > contentAboveView - 400) break;
            trimHeight += h;
            trimCount++;
          }
          if (trimCount > 0) {
            this.vsTopSpacer += trimHeight;
            this.vsStart += trimCount;
            this.vsRenderCount -= trimCount;
          }
        }
      }

      // === RESTORE: scrolling up near spacer, bring back items ===
      if (this.vsStart > 0 && scrollTop < this.vsTopSpacer + 300) {
        const restoreCount = Math.min(growBatch, this.vsStart);
        const newStart = this.vsStart - restoreCount;
        let restoreHeight = 0;
        for (let i = newStart; i < this.vsStart; i++) {
          restoreHeight += this.vsHeightCache[i] || 50;
        }
        this.vsTopSpacer = Math.max(0, this.vsTopSpacer - restoreHeight);
        this.vsStart = newStart;
        this.vsRenderCount += restoreCount;
      }
    },

    /** Measure rendered location group heights and cache them */
    _vsMeasure(el) {
      const groups = el.querySelectorAll('.grid-location-group');
      for (let i = 0; i < groups.length; i++) {
        this.vsHeightCache[this.vsStart + i] = groups[i].offsetHeight;
      }
    },

    /** Reset virtual scroll state */
    vsReset() {
      this.vsStart = 0;
      this.vsTopSpacer = 0;
      this.vsHeightCache = {};
      this.vsRenderCount = this.showHourly ? 20 : 80;
      this.currentPage = 1;
      this.$nextTick(() => {
        if (this.$refs.gridScrollContainer) {
          this.$refs.gridScrollContainer.scrollTop = 0;
        }
      });
    },

    /** Toggle the Weather Alert shortcut criterion */
    toggleAlertShortcut() {
      const current = this.activeRuleset?.criteria
        ? [...this.activeRuleset.criteria]
        : [];

      const idx = current.findIndex(c => c.field === 'alerts' && c.operator === 'present');
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        const criterion = createCriterion('alerts', 'present', true, 'days', 'Weather Alert', { color: 'danger' });
        current.push(criterion);
      }

      this.setCriteria(current, 'OR', { skipInvalidation: this.hasRun });
      this.persistCriteria();
    },
    getDayIssues(day) {
      if (!day.matchedCriteria?.length) return '';
      const issues = [];
      for (const cid of day.matchedCriteria) {
        const criterion = this.activeRuleset?.criteria?.find(c => c.id === cid);
        if (criterion?.name) issues.push(criterion.name);
      }
      return issues.join(', ');
    },

    /** Get matched criteria as full objects (with name, color) for a day */
    getMatchedCriteriaObjects(day) {
      if (!day.matchedCriteria?.length) return [];
      const objects = [];
      for (const cid of day.matchedCriteria) {
        const criterion = this.activeRuleset?.criteria?.find(c => c.id === cid);
        if (criterion) objects.push(criterion);
      }
      return objects;
    },

    /** Get unique triggered criteria across all days for a location result */
    getLocationTriggeredCriteria(result) {
      if (!result.triggeredCriteria?.length) return [];
      return result.triggeredCriteria
        .map(cid => this.activeRuleset?.criteria?.find(c => c.id === cid))
        .filter(Boolean);
    },

    /** Badge class for a criterion chip */
    getCriterionBadgeClass(c) {
      return this.getCriterionBadgeClassByColor(c.color);
    },

    /** Badge class by color name */
    getCriterionBadgeClassByColor(color) {
      const colorMap = {
        danger: 'bg-danger',
        warning: 'bg-warning text-dark',
        info: 'bg-info',
        success: 'bg-success',
        purple: 'bg-purple',
        orange: 'bg-orange',
        pink: 'bg-pink',
        teal: 'bg-teal',
        indigo: 'bg-indigo',
        cyan: 'bg-cyan',
        lime: 'bg-lime'
      };
      return colorMap[color] || 'bg-secondary';
    },

    /** Format a criterion for display when it has no custom name */
    formatCriterionLabel(c) {
      return formatCriterionLabel(c, this.unitGroup);
    },

    /** Open site detail modal */
    showSiteDetails(result) {
      this.selectedSite = result;
      this.siteDetailTab = 'risks';
      this.forecastExpandedDay = null;
      this.siteMapReady = false;
      this.hourlyExpandedDay = null;
      // Auto-expand the first alert so the user immediately sees detail
      this.expandedAlertIdx = (result.alertsData && result.alertsData.length > 0) ? 0 : -1;
    },

    /** Switch to grid view and expand the location's row (called from map "View Full Report") */
    showSiteGridReport(result) {
      this.viewMode = 'grid';
      // Ensure the location is expanded in the grid
      const rid = result.id || result.location;
      if (!this.expandedLocationIds.includes(rid)) {
        this.expandedLocationIds.push(rid);
      }
      // Scroll the grid into view after Vue re-renders
      this.$nextTick(() => {
        const el = document.querySelector(`[data-location-id=\"${CSS.escape(rid)}\"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    /** Close site detail modal */
    closeSiteDetail() {
      this.selectedSite = null;
      this.siteDetailTab = 'risks';
      this.forecastExpandedDay = null;
      this.siteMapReady = false;
      this.hourlyExpandedDay = null;
      this.expandedAlertIdx = -1;
    },

    /** Toggle alert expand/collapse */
    toggleAlertExpand(idx) {
      this.expandedAlertIdx = this.expandedAlertIdx === idx ? -1 : idx;
    },

    /** Handle cell click in the date grid */
    handleCellClick({ result, date }) {
      this.showSiteDetails(result);
      // Auto-expand the clicked date's hourly view if available
      if (date) {
        this.$nextTick(() => {
          this.hourlyExpandedDay = date;
        });
      }
    },

    /** Toggle per-location element expansion */
    toggleLocationExpand(result) {
      const idx = this.expandedLocationIds.indexOf(result.id);
      if (idx >= 0) {
        this.expandedLocationIds.splice(idx, 1);
      } else {
        this.expandedLocationIds.push(result.id);
      }
    },

    /** Toggle expand/collapse all locations */
    toggleExpandAll() {
      if (this.allExpanded) {
        this.expandedLocationIds = [];
        this.expandAllSetting = false;
      } else {
        this.expandedLocationIds = this.locationResults.map(r => r.id);
        this.expandAllSetting = true;
      }
      try { localStorage.setItem('flexTracker_expandAll', String(this.expandAllSetting)); } catch (e) { /* ignore */ }
    },

    /** Get button class for criteria filter (outline) */
    getCriterionBtnClass(c) {
      const colorMap = {
        danger: 'btn-outline-danger',
        warning: 'btn-outline-warning',
        info: 'btn-outline-info',
        success: 'btn-outline-success',
        purple: 'btn-outline-secondary',
        orange: 'btn-outline-warning',
        pink: 'btn-outline-secondary',
        teal: 'btn-outline-info',
        indigo: 'btn-outline-primary',
        cyan: 'btn-outline-info',
        lime: 'btn-outline-success'
      };
      return colorMap[c.color] || 'btn-outline-secondary';
    },

    /** Get outline badge class for criteria filter (inactive state) */
    getCriterionOutlineBadgeClass(c) {
      const colorMap = {
        danger: 'bg-light text-danger border border-danger',
        warning: 'bg-light text-dark border border-warning',
        info: 'bg-light text-info border border-info',
        success: 'bg-light text-success border border-success',
        purple: 'bg-light text-purple border border-purple',
        orange: 'bg-light text-orange border border-orange',
        pink: 'bg-light text-pink border border-pink',
        teal: 'bg-light text-teal border border-teal',
        indigo: 'bg-light text-indigo border border-indigo',
        cyan: 'bg-light text-cyan border border-cyan',
        lime: 'bg-light text-lime border border-lime'
      };
      return colorMap[c.color] || 'bg-light text-dark border';
    },

    /** Set the active filter card */
    setFilterCard(type, criterionId) {
      if (type === 'criteria') {
        this.criteriaFilter = this.criteriaFilter === criterionId ? null : criterionId;
        this.filter = 'all';
      } else {
        this.criteriaFilter = null;
        this.filter = this.filter === type && type !== 'all' ? 'all' : type;
      }
    },

    /** CSS class for a criterion filter card based on its color */
    criteriaCardClass(c) {
      const map = {
        danger: 'filter-card-danger',
        warning: 'filter-card-warning',
        info: 'filter-card-info',
        success: 'filter-card-success',
        purple: 'filter-card-purple',
        orange: 'filter-card-orange',
        pink: 'filter-card-pink',
        teal: 'filter-card-teal',
        indigo: 'filter-card-indigo',
        cyan: 'filter-card-cyan',
        lime: 'filter-card-lime'
      };
      return map[c.color] || '';
    },

    /** Inline style for a criterion filter card — colored top accent + tinted bg */
    criteriaCardStyle(c) {
      const hex = this.getCriterionHexColor(c.color);
      return {
        borderTopColor: hex,
        backgroundColor: hex + '08'
      };
    },

    /** Get button class for criteria filter (active) */
    getCriterionBtnActiveClass(c) {
      const colorMap = {
        danger: 'btn-danger',
        warning: 'btn-warning',
        info: 'btn-info',
        success: 'btn-success',
        purple: 'btn-secondary',
        orange: 'btn-warning',
        pink: 'btn-secondary',
        teal: 'btn-info',
        indigo: 'btn-primary',
        cyan: 'btn-info',
        lime: 'btn-success'
      };
      return colorMap[c.color] || 'btn-secondary';
    },

    // ============================================
    // Hourly View
    // ============================================

    /** Toggle hourly expansion for a day row */
    toggleHourlyView(day) {
      if (this.hourlyExpandedDay === day.datetime) {
        this.hourlyExpandedDay = null;
      } else {
        this.hourlyExpandedDay = day.datetime;
      }
    },

    /** Format hour label from datetime (e.g., "08:00:00" → "8AM") */
    formatHourLabel(hourDatetime) {
      if (!hourDatetime) return '';
      const parts = hourDatetime.split(':');
      const h = parseInt(parts[0], 10);
      if (h === 0) return '12AM';
      if (h === 12) return '12PM';
      return h > 12 ? `${h - 12}PM` : `${h}AM`;
    },

    /** Format a weather value with appropriate unit suffix */
    formatValue(val, type) {
      if (val === undefined || val === null) return '-';
      if (type === 'speed') {
        const unit = this.unitGroup === 'us' ? ' mph' : ' km/h';
        return Math.round(val) + unit;
      }
      if (type === 'temp') {
        const unit = this.unitGroup === 'us' ? '°F' : '°C';
        return Math.round(val) + unit;
      }
      if (type === 'precip') {
        const unit = this.unitGroup === 'us' ? ' in' : ' mm';
        return val > 0 ? val.toFixed(1) + unit : '0' + unit;
      }
      if (type === 'percent') {
        return Math.round(val) + '%';
      }
      return val;
    },

    /** Check if a daily row falls within any alert's onset–ends range */
    isDayInAlertRange(day) {
      if (!this.selectedSite?.alertsData?.length || !day?.datetime) return false;
      const dayStart = new Date(day.datetime + 'T00:00:00');
      const dayEnd = new Date(day.datetime + 'T23:59:59');
      return this.selectedSite.alertsData.some(alert => {
        const onset = alert.onset ? new Date(alert.onset) : null;
        const ends = alert.ends ? new Date(alert.ends) : null;
        const beforeEnd = !onset || onset <= dayEnd;
        const afterStart = !ends || ends >= dayStart;
        return beforeEnd && afterStart;
      });
    },

    /** Check if a specific hour falls within any alert's onset–ends range */
    isHourInAlertRange(day, hour) {
      if (!this.selectedSite?.alertsData?.length || !day?.datetime || !hour?.datetime) return false;
      const hh = hour.datetime.split(':')[0];
      const hourStart = new Date(day.datetime + 'T' + hh.padStart(2, '0') + ':00:00');
      const hourEnd = new Date(day.datetime + 'T' + hh.padStart(2, '0') + ':59:59');
      return this.selectedSite.alertsData.some(alert => {
        const onset = alert.onset ? new Date(alert.onset) : null;
        const ends = alert.ends ? new Date(alert.ends) : null;
        const beforeEnd = !onset || onset <= hourEnd;
        const afterStart = !ends || ends >= hourStart;
        return beforeEnd && afterStart;
      });
    },

    /** Count how many alerts are active during a specific hour */
    countHourActiveAlerts(day, hour) {
      if (!this.selectedSite?.alertsData?.length || !day?.datetime || !hour?.datetime) return 0;
      const hh = hour.datetime.split(':')[0];
      const hourStart = new Date(day.datetime + 'T' + hh.padStart(2, '0') + ':00:00');
      const hourEnd = new Date(day.datetime + 'T' + hh.padStart(2, '0') + ':59:59');
      let count = 0;
      for (const alert of this.selectedSite.alertsData) {
        const onset = alert.onset ? new Date(alert.onset) : null;
        const ends = alert.ends ? new Date(alert.ends) : null;
        const beforeEnd = !onset || onset <= hourEnd;
        const afterStart = !ends || ends >= hourStart;
        if (beforeEnd && afterStart) count++;
      }
      return count;
    },

    // ============================================
    // Confirmation Dialog
    // ============================================

    showConfirm(action, message, detail) {
      this.confirmDialog = { show: true, action, message, detail: detail || '' };
    },

    executeConfirm() {
      const action = this.confirmDialog.action;
      this.confirmDialog.show = false;

      if (action === 'clearLocations') {
        this.$emit('update:modelValue', []);
        this.locations = [];
      } else if (action === 'clearCriteria') {
        this.editingCriteria = [];
      }
    },

    // ============================================
    // Severity Level Configuration
    // ============================================

    /** Reset severity levels to defaults */
    resetSeverityLevels() {
      this.severityLevels = {
        ok: { label: 'Clear', color: '#d1e7dd', textColor: '#0a3622', badgeClass: 'bg-success' },
        caution: { label: 'Caution', color: '#fff3cd', textColor: '#664d03', badgeClass: 'bg-warning text-dark' },
        flag: { label: 'Threshold', color: '#f8d7da', textColor: '#58151c', badgeClass: 'bg-danger' }
      };
      this.persistSeverityLevels();
    },

    /** Persist severity levels to localStorage */
    persistSeverityLevels() {
      try {
        localStorage.setItem('flexTracker_severityLevels', JSON.stringify(this.severityLevels));
      } catch (e) { /* ignore */ }
    },

    /** Load persisted severity levels */
    loadPersistedSeverityLevels() {
      try {
        const saved = localStorage.getItem('flexTracker_severityLevels');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.ok && parsed.caution && parsed.flag) {
            this.severityLevels = parsed;
          }
        }
      } catch (e) { /* ignore */ }
    },

    /** Persist business hours to localStorage */
    persistBusinessHours() {
      try {
        localStorage.setItem('flexTracker_businessHours', JSON.stringify(this.businessHours));
      } catch (e) { /* ignore */ }
    },

    /** Load persisted business hours */
    loadPersistedBusinessHours() {
      try {
        const saved = localStorage.getItem('flexTracker_businessHours');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (typeof parsed.enabled === 'boolean') {
            this.businessHours = parsed;
          }
        }
      } catch (e) { /* ignore */ }
    },

    /** Load persisted view settings (viewMode, expandAll) */
    loadViewSettings() {
      try {
        const vm = localStorage.getItem('flexTracker_viewMode');
        if (vm && ['grid', 'map', 'list'].includes(vm)) this.viewMode = vm;
        const ea = localStorage.getItem('flexTracker_expandAll');
        if (ea !== null) this.expandAllSetting = ea === 'true';
        const sh = localStorage.getItem('flexTracker_showHourly');
        if (sh !== null) this.showHourly = sh === 'true';
      } catch (e) { /* ignore */ }
    },

    // ============================================
    // Site Detail Modal Helpers
    // ============================================

    /** Select a day for hourly gantt display (risks tab) */
    selectDayForHourly(day) {
      this.hourlyExpandedDay = this.hourlyExpandedDay === day.datetime ? null : day.datetime;
    },

    /** Toggle forecast day expansion (forecast tab) */
    toggleForecastDay(day) {
      this.forecastExpandedDay = this.forecastExpandedDay === day.datetime ? null : day.datetime;
    },

    /** Switch to site map tab — delay rendering so the container is visible */
    switchToSiteMap() {
      this.siteDetailTab = 'map';
      this.siteMapReady = false;
      this.$nextTick(() => {
        this.siteMapReady = true;
      });
    },

    /** Check if a criterion is flagged for a specific day */
    isDayCriterionFlagged(day, criterion) {
      if (!(day.matchedCriteria || []).includes(criterion.id)) return false;
      // Guard against timezone-shifted days where the server marks criteria
      // but the actual field value is missing (e.g. PST location still on
      // "yesterday" when queried just after midnight EST).
      if (criterion.field && criterion.field !== 'alerts') {
        const val = day[criterion.field];
        if (val === undefined || val === null) return false;
      }
      return true;
    },

    /** Check if a criterion is flagged for a specific hour */
    isHourCriterionFlagged(day, hour, criterion) {
      const hourTime = hour.datetime;
      const hourCriteria = day.hourCriteriaMap?.[hourTime] || [];
      return hourCriteria.includes(criterion.id);
    },

    /** Get the value of a criterion's field for a specific hour */
    getHourCriterionValue(hour, criterion) {
      if (!hour || !criterion?.field) return '-';
      const val = hour[criterion.field];
      if (val === undefined || val === null) return '-';
      return Math.round(val * 10) / 10;
    },

    /** Get display value for a criterion on a specific day (used in popup daily Gantt) */
    getDayCriterionDisplayValue(day, criterion) {
      if (!day || !criterion?.field) return '-';
      if (criterion.field === 'alerts') {
        // If raw alert objects are available (client preview), count actual overlapping alerts
        if (this.selectedSite?.alertsData?.length) {
          const dayStart = new Date(day.datetime + 'T00:00:00');
          const dayEnd = new Date(day.datetime + 'T23:59:59');
          let count = 0;
          for (const alert of this.selectedSite.alertsData) {
            const onset = alert.onset ? new Date(alert.onset) : null;
            const ends = alert.ends ? new Date(alert.ends) : null;
            if ((!onset || onset <= dayEnd) && (!ends || ends >= dayStart)) count++;
          }
          return count > 0 ? count : '0';
        }
        // Server path: check if alert criterion matched this day
        return (day.matchedCriteria || []).includes(criterion.id) ? 1 : '0';
      }
      const val = day[criterion.field];
      if (val === undefined || val === null) return '-';
      if (['tempmax', 'tempmin', 'temp'].includes(criterion.field)) return Math.round(val) + '°';
      if (['precipprob'].includes(criterion.field)) return Math.round(val) + '%';
      if (['windspeed', 'windgust'].includes(criterion.field)) return Math.round(val);
      if (['precip', 'snow', 'snowdepth'].includes(criterion.field)) return val > 0 ? val.toFixed(1) : '0';
      if (['humidity'].includes(criterion.field)) return Math.round(val) + '%';
      if (criterion.field === 'conditions') return val.length > 5 ? val.substring(0, 4) + '…' : val;
      return val;
    },

    /** Get display value for a criterion at a specific hour (used in popup hourly Gantt) */
    getHourCriterionDisplayValue(hour, criterion) {
      if (!hour || !criterion?.field) return '-';
      if (criterion.field === 'alerts') {
        // If raw alert objects are available (client preview), count actual overlapping alerts
        if (this.selectedSite?.alertsData?.length) {
          return this.countHourActiveAlerts(this.selectedDayData, hour);
        }
        // Server path: check if alert criterion matched this hour
        return (hour.matchedCriteria || []).includes(criterion.id) ? 1 : 0;
      }
      const field = (criterion.field === 'tempmax' || criterion.field === 'tempmin') ? 'temp' : criterion.field;
      const val = hour[field];
      if (val === undefined || val === null) return '-';
      if (['temp', 'tempmax', 'tempmin'].includes(field)) return Math.round(val) + '°';
      if (field === 'precipprob') return Math.round(val) + '%';
      if (['windspeed', 'windgust'].includes(field)) return Math.round(val);
      if (['precip', 'snow', 'snowdepth'].includes(field)) return val > 0 ? val.toFixed(1) : '0';
      if (['humidity'].includes(field)) return Math.round(val) + '%';
      return val;
    },

    /** Get criteria that matched on the currently selected day (for badges in day detail) */
    getSelectedDayMatchedCriteria() {
      if (!this.selectedDayData?.matchedCriteria?.length) return [];
      return this.selectedDayData.matchedCriteria
        .map(cid => this.activeCriteriaList.find(c => c.id === cid))
        .filter(Boolean);
    },

    /** Get a UV index label */
    getUVLabel(uv) {
      if (uv == null) return '';
      if (uv <= 2) return 'Low';
      if (uv <= 5) return 'Moderate';
      if (uv <= 7) return 'High';
      if (uv <= 10) return 'Very High';
      return 'Extreme';
    },

    /** Format a time string (HH:MM:SS) to short form */
    formatTimeShort(timeStr) {
      if (!timeStr) return '';
      const parts = timeStr.split(':');
      const h = parseInt(parts[0], 10);
      const m = parts[1] || '00';
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${m} ${ampm}`;
    },

    /** Draw sparkline chart for selected day's hourly data */
    drawSparklines() {
      const canvas = this.$refs.sparklineCanvas;
      if (!canvas || !this.selectedDayData?.hours?.length) return;
      const ctx = canvas.getContext('2d');
      const hours = this.selectedDayData.hours;
      const dayData = this.selectedDayData;
      const W = canvas.width;
      const H = canvas.height;
      const pad = { top: 10, bottom: 20, left: 5, right: 5 };
      const plotW = W - pad.left - pad.right;
      const plotH = H - pad.top - pad.bottom;
      
      ctx.clearRect(0, 0, W, H);

      // Collect temp data
      const temps = hours.map(h => h.temp).filter(t => t != null);
      if (temps.length === 0) return;
      const tMin = Math.min(...temps) - 2;
      const tMax = Math.max(...temps) + 2;
      const tRange = tMax - tMin || 1;
      
      const xStep = plotW / (hours.length - 1);
      
      // Draw per-criterion alert stripes for each flagged hour
      const criteria = this.activeCriteriaList;
      const hCriteriaMap = dayData.hourCriteriaMap || {};
      for (let i = 0; i < hours.length; i++) {
        const hour = hours[i];
        const hKey = hour.datetime;
        const triggeredIds = hCriteriaMap[hKey] || [];
        const isAlertHour = dayData.alertMatchedHours?.includes(hKey);
        if (triggeredIds.length === 0 && !isAlertHour) continue;
        const x0 = pad.left + i * xStep - xStep / 2;
        // Draw a colored stripe for each triggered criterion, stacked vertically
        const colors = [];
        triggeredIds.forEach(cid => {
          const c = criteria.find(cr => cr.id === cid);
          if (c) colors.push(this.getCriterionHexColor(c.color));
        });
        if (isAlertHour && !colors.length) colors.push(this.getCriterionHexColor('warning'));
        if (colors.length === 1) {
          ctx.fillStyle = this.hexToRgba(colors[0], 0.3);
          ctx.fillRect(x0, pad.top, xStep, plotH);
        } else {
          const stripeH = plotH / colors.length;
          colors.forEach((clr, ci) => {
            ctx.fillStyle = this.hexToRgba(clr, 0.3);
            ctx.fillRect(x0, pad.top + ci * stripeH, xStep, stripeH);
          });
        }
        // Draw a coloured tick at the bottom to mark the alert hour
        ctx.fillStyle = this.hexToRgba(colors[0], 0.8);
        ctx.fillRect(x0, pad.top + plotH - 3, xStep, 3);
      }

      // Temperature line
      ctx.beginPath();
      ctx.strokeStyle = '#dc3545';
      ctx.lineWidth = 2;
      for (let i = 0; i < hours.length; i++) {
        const t = hours[i].temp;
        if (t == null) continue;
        const x = pad.left + i * xStep;
        const y = pad.top + plotH - ((t - tMin) / tRange) * plotH;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Precip prob line (scaled 0–100 to plot area)
      const hasPrec = hours.some(h => h.precipprob > 0);
      if (hasPrec) {
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(13, 110, 253, 0.6)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([3, 3]);
        for (let i = 0; i < hours.length; i++) {
          const p = hours[i].precipprob || 0;
          const x = pad.left + i * xStep;
          const y = pad.top + plotH - (p / 100) * plotH;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // X-axis hour labels
      ctx.fillStyle = '#767676';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      for (let i = 0; i < hours.length; i += 3) {
        const x = pad.left + i * xStep;
        const label = hours[i].datetime?.substring(0, 2) || '';
        ctx.fillText(label, x, H - 4);
      }

      // Legend
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#dc3545';
      ctx.fillText('— Temp', pad.left + 2, pad.top + 10);
      if (hasPrec) {
        ctx.fillStyle = 'rgba(13, 110, 253, 0.8)';
        ctx.fillText('--- Precip %', pad.left + 50, pad.top + 10);
      }
    },

    /** Convert a hex color to rgba */
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    },

    /** Check if an hour is outside business hours */
    isOutsideBusinessHours(hour) {
      if (!this.businessHours.enabled || !hour?.datetime) return false;
      const hourNum = parseInt(hour.datetime.split(':')[0], 10);
      const startH = parseInt(this.businessHours.start.split(':')[0], 10);
      const endH = parseInt(this.businessHours.end.split(':')[0], 10);
      return hourNum < startH || hourNum >= endH;
    },

    /** Map criterion color name to hex */
    getCriterionHexColor(color) {
      const map = {
        danger: '#dc3545', warning: '#ffc107', info: '#3c76f2', success: '#198754',
        purple: '#6f42c1', orange: '#fd7e14', pink: '#d63384', teal: '#20c997',
        indigo: '#6610f2', cyan: '#0891b2', lime: '#84cc16'
      };
      return map[color] || '#6c757d';
    },

    /** Get map center for a location result */
    getSiteCenter(result) {
      if (result?.latitude && result?.longitude) {
        return [result.latitude, result.longitude];
      }
      return [39.8283, -98.5795];
    },

    /** Format short day name (e.g., "Mon") */
    formatDayShort(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short' });
    },

    /** Format day number (e.g., "15") */
    formatDayNum(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime + 'T12:00:00');
      return d.getDate();
    },

    /** Get weather icon URL */
    getWeatherIconUrl(icon) {
      if (!icon) return '';
      return `https://raw.githubusercontent.com/visualcrossing/WeatherIcons/main/PNG/2nd%20Set%20-%20Color/${icon}.png`;
    },

    /** Preview: run exception check on at most 5 locations client-side */
    async doPreview() {
      await this.runExceptionCheck({ maxLocations: 5 });
      this.captureRunFingerprint();
      if (this.expandAllSetting && this.locationResults?.length) {
        this.expandedLocationIds = this.locationResults.map(r => r.id);
      }
    },

    /** Change date range — keeps existing results visible so stale indicator works */
    changeDateRange(period) {
      this.setDateRange(period, null, { skipInvalidation: this.hasRun });
    },

    /** Change unit group — keeps existing results visible so stale indicator works */
    changeUnitGroup(newGroup) {
      this.setUnitGroup(newGroup, { skipInvalidation: this.hasRun });
    },

    /** Snapshot the current definition fingerprint for stale detection */
    captureRunFingerprint() {
      this.$nextTick(() => {
        this.lastRunFingerprint = this.currentDefinitionFingerprint;
      });
    },

    /**
     * Build a canonical snapshot of the full job definition.
     * This mirrors the exact structure submitted when saving/running so
     * the stale indicator is 100 % aligned with the persisted definition.
     */
    buildDefinitionSnapshot() {
      const criteria = (this.activeCriteriaList || []).map(c => {
        const crit = {
          element: c.field,
          operator: c.operator,
          value: typeof c.value === 'number' ? c.value : 0,
          granularity: c.granularity || 'daily',
          color: c.color || 'warning',
          enabled: c.enabled !== false
        };
        if (c.operator === 'between' && Array.isArray(c.value)) {
          crit.value = typeof c.value[0] === 'number' ? c.value[0] : 0;
          crit.value2 = typeof c.value[1] === 'number' ? c.value[1] : 0;
        }
        return crit;
      });

      const locations = (this.locations || []).map(loc => {
        if (typeof loc === 'string') return loc;
        return loc.location || loc.address || loc.id || String(loc);
      });

      return {
        criteria,
        combinationMethod: (this.activeRuleset?.combineMode || 'OR').toLowerCase(),
        businessHours: {
          enabled: !!this.businessHours?.enabled,
          start: this.businessHours?.start || '08:00',
          end: this.businessHours?.end || '17:00'
        },
        locations,
        startDate: this.startPeriod || 'next7days',
        unitGroup: this.unitGroup || 'us'
      };
    },

    /** Reset everything to start a fresh definition */
    newDefinition() {
      this.clearLocations();
      this.setCriteria([], 'OR');
      this.businessHours = { enabled: false, start: '08:00', end: '17:00' };
      this.setBusinessHours(this.businessHours);
      this.startPeriod = 'next7days';
      this.unitGroup = 'us';
      this.localMetadata = null;
      this._defnLoadedFromServer = false;
      this.lastExecutedDate = null;
      this.lastRunFingerprint = null;
      this.saveDefnName = '';
      this.saveDefnDesc = '';
      this.scheduleRunTimes = [];
      this.scheduleEnabled = true;
      this.showScheduleEditor = false;
      this.persistLocations();
      this.persistCriteria();
      this.persistBusinessHours();
      this.persistMetadata();
    },

    /**
     * Clear all component-level state when the user session changes.
     * The engine composable already clears locations/criteria/results
     * via its own terminatesession handler; this method clears the
     * FlexibleWeatherTracker-specific UI and task state.
     */
    resetForSessionChange() {
      // Stop any active polling
      if (this.pollingState.pollingTimer) {
        clearTimeout(this.pollingState.pollingTimer);
        this.pollingState.pollingTimer = null;
      }
      this.pollingState.isPolling = false;
      this.pollingState.pollingRetries = 0;
      this.pollingState.hasExceedRetries = false;
      this.pollingState.isRefreshingResults = false;

      // Clear task / definition state
      this.localMetadata = null;
      this.taskInstance = null;
      this._defnLoadedFromServer = false;
      this.lastExecutedDate = null;
      this.lastRunFingerprint = null;
      this.saveDefnName = '';
      this.saveDefnDesc = '';
      this.saveResultMessage = null;
      this.saveResultIsError = false;
      this.scheduleRunTimes = [];
      this.scheduleEnabled = true;
      this.showScheduleEditor = false;

      // Close any open dialogs/panels
      this.selectedSite = null;
      this.showSaveDefinitionModal = false;
      this.showBulkDefinitionsList = false;
      this.showShareManager = false;
      this.showCriteriaModal = false;
      this.savedDefinitions = [];

      // Reset view
      this.showEditor = true;
      this.isSavingBulkTask = false;
      this.isLoadingResult = false;
      this.silentSaveMode = false;

      // Clear localStorage references (they belong to the previous user)
      try {
        localStorage.removeItem('flexTracker_metadata');
        localStorage.removeItem('flexTracker_locations');
        localStorage.removeItem('flexTracker_criteria');
        localStorage.removeItem('flexTracker_businessHours');
      } catch (e) { /* ignore */ }
    },

    /** Submit or silently save: if we already know the ID, skip the modal and save immediately */
    async submitOrSilentSave() {
      this.pendingRunOnce = true;
      const vc = window.vcCommunicator;
      if (!vc || !vc.sessionId) {
        this.saveResultMessage = 'Please sign in to save definitions.';
        this.saveResultIsError = true;
        this.showSaveDefinitionModal = true;
        return;
      }
      if (this.localMetadata && this.localMetadata.id) {
        // Silent save — like Ctrl+S in Word: overwrite existing, no prompts, no modal
        this.silentSaveMode = true;
        this.resetPollingState();
        await this.saveAsBulkTask();
      } else {
        // No known ID — show the name/description modal for initial submission
        this.openSaveDefinitionModal();
      }
    },

    /** Save only (no run): saves the definition with runOnce=false */
    async saveOnly() {
      this.pendingRunOnce = false;
      const vc = window.vcCommunicator;
      if (!vc || !vc.sessionId) {
        this.saveResultMessage = 'Please sign in to save definitions.';
        this.saveResultIsError = true;
        this.showSaveDefinitionModal = true;
        return;
      }
      if (this.localMetadata && this.localMetadata.id) {
        // Silent save — overwrite existing, no prompts
        this.silentSaveMode = true;
        this.resetPollingState();
        await this.saveAsBulkTask();
      } else {
        // No known ID — show the name/description modal
        this.openSaveDefinitionModal();
      }
    },

    /** Save As: always show the popup for a new name, creating a copy (runOnce=false) */
    saveAsNew() {
      this.pendingRunOnce = false;
      const vc = window.vcCommunicator;
      if (!vc || !vc.sessionId) {
        this.saveResultMessage = 'Please sign in to save definitions.';
        this.saveResultIsError = true;
        this.showSaveDefinitionModal = true;
        return;
      }
      // Clear the ID so it creates a new copy instead of overwriting
      this.localMetadata = this.localMetadata ? { ...this.localMetadata, id: undefined } : null;
      this.persistMetadata();
      this.openSaveDefinitionModal();
    },

    /** Open the Save Definition modal with pre-filled defaults (new definitions only) */
    openSaveDefinitionModal() {
      const locCount = this.locations.length;
      const critCount = this.activeCriteriaList.length;
      this.saveDefnName = `Weather Exceptions - ${locCount} location${locCount !== 1 ? 's' : ''}`;
      this.saveDefnDesc = `${critCount} criteria, ${this.unitGroup} units`;
      this.saveResultMessage = null;
      this.saveResultIsError = false;
      this.resetPollingState();
      this.showSaveDefinitionModal = true;
    },

    /** Reset polling state for a fresh submit */
    resetPollingState() {
      this.taskInstance = null;
      this.pollingState.isPolling = false;
      this.pollingState.pollingRetries = 0;
      this.pollingState.hasExceedRetries = false;
      this.pollingState.isRefreshingResults = false;
      if (this.pollingState.pollingTimer) {
        clearTimeout(this.pollingState.pollingTimer);
        this.pollingState.pollingTimer = null;
      }
    },

    // ============================================
    // Schedule helpers
    // ============================================

    /** Sort schedule run times in ascending order */
    sortScheduleRunTimes() {
      this.scheduleRunTimes.sort((a, b) => {
        const [ah, am] = a.split(':').map(Number);
        const [bh, bm] = b.split(':').map(Number);
        return ah !== bh ? ah - bh : am - bm;
      });
    },

    /** Add a new run time to the schedule */
    addScheduleRunTime() {
      let newTime = '06:00';
      if (this.scheduleRunTimes.length > 0) {
        const sorted = [...this.scheduleRunTimes].sort((a, b) => {
          const [ah, am] = a.split(':').map(Number);
          const [bh, bm] = b.split(':').map(Number);
          return ah !== bh ? ah - bh : am - bm;
        });
        const latest = sorted[sorted.length - 1];
        let [hour, minute] = latest.split(':').map(Number);
        let nextHour = hour + 6;
        if (nextHour >= 24) nextHour = hour;
        newTime = `${String(nextHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      }
      this.scheduleRunTimes.push(newTime);
      this.sortScheduleRunTimes();
    },

    /** Remove a run time at the given index */
    removeScheduleRunTime(idx) {
      this.scheduleRunTimes.splice(idx, 1);
      this.sortScheduleRunTimes();
    },

    /** Get hour from a "HH:MM" time string */
    getScheduleHour(time) {
      return parseInt(time.split(':')[0]) || 0;
    },

    /** Get minute from a "HH:MM" time string */
    getScheduleMinute(time) {
      return parseInt(time.split(':')[1]) || 0;
    },

    /** Update the hour portion of a schedule run time */
    setScheduleHour(idx, hour) {
      const minute = this.getScheduleMinute(this.scheduleRunTimes[idx]);
      this.scheduleRunTimes[idx] = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      this.sortScheduleRunTimes();
    },

    /** Update the minute portion of a schedule run time */
    setScheduleMinute(idx, minute) {
      const hour = this.getScheduleHour(this.scheduleRunTimes[idx]);
      this.scheduleRunTimes[idx] = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      this.sortScheduleRunTimes();
    },

    /** Format schedule times for summary display */
    formatScheduleSummary() {
      if (!this.scheduleRunTimes.length) return '';
      return this.scheduleRunTimes.join(', ');
    },

    /** Confirm overwriting an existing definition */
    confirmOverwrite() {
      this.showConfirmOverwrite = false;
      if (this.overwriteResolve) {
        this.localMetadata = this.existingPackageToOverwrite;
        this.overwriteResolve(true);
        this.overwriteResolve = null;
      }
    },

    /** Cancel overwriting — keep the form open */
    cancelOverwrite() {
      this.showConfirmOverwrite = false;
      if (this.overwriteResolve) {
        this.overwriteResolve(false);
        this.overwriteResolve = null;
      }
    },

    /** Close the Save Definition modal and stop any active polling */
    closeSaveModal() {
      this.showSaveDefinitionModal = false;
      if (this.pollingState.pollingTimer) {
        clearTimeout(this.pollingState.pollingTimer);
        this.pollingState.pollingTimer = null;
      }
      this.pollingState.isRefreshingResults = false;
      this.pollingState.isPolling = false;
    },

    /** Save the current configuration as a Bulk Task Job Package (called from modal) */
    async saveAsBulkTask() {
      if (!this.canSaveAsBulkTask || !this.saveDefnName.trim()) return;
      this.isSavingBulkTask = true;
      this.saveResultMessage = null;
      try {
        // Build the exception definition from current criteria, combine mode, business hours
        const criteria = (this.activeRuleset?.criteria || []).map(c => {
          // Ensure value is always numeric for backend (double field).
          // Operators like present/notpresent may have boolean or null values.
          let numValue = typeof c.value === 'number' ? c.value : 0;
          const crit = {
            id: c.id || null,
            element: c.field,
            operator: c.operator,
            value: numValue,
            granularity: c.granularity || 'daily',
            name: c.name || null,
            color: c.color || 'warning',
            enabled: c.enabled !== false,
            isStub: !!c.isStub
          };
          // Backend expects separate value/value2 for 'between' operator
          if (c.operator === 'between' && Array.isArray(c.value)) {
            crit.value = typeof c.value[0] === 'number' ? c.value[0] : 0;
            crit.value2 = typeof c.value[1] === 'number' ? c.value[1] : 0;
          }
          return crit;
        });

        const exceptionDefinition = {
          criteria,
          combinationMethod: (this.activeRuleset?.combineMode || 'OR').toLowerCase(),
          businessHours: {
            enabled: !!this.businessHours.enabled,
            start: this.businessHours.start || '08:00',
            end: this.businessHours.end || '17:00'
          },
          notificationTrigger: 'any_exception'
        };

        // Build locations array in the bulk package format (must be {location, name} objects)
        const locations = this.locations.map(loc => {
          if (typeof loc === 'string') return { location: loc, name: loc };
          return { location: loc.location || loc.address || loc.id || String(loc), name: loc.name || loc.location || null };
        });

        // Build the package
        const localPackage = {
          params: {
            goal: 'weatherexceptions',
            startdate: this.startPeriod || 'next7days',
            enddate: null,
            unitGroup: this.unitGroup || 'us',
            contentType: 'flatjson',
            options: '',
            include: '',
            elements: '',
            maxDistance: -1,
            exceptiondefinition: JSON.stringify(exceptionDefinition)
          },
          locations,
          notificationConfigs: [],
          runOnce: this.pendingRunOnce,
          enabled: this.scheduleRunTimes.length > 0 ? this.scheduleEnabled : true,
          runTimes: [...this.scheduleRunTimes],
          type: 4,
          accountUserId: window.vcCommunicator?.sessionState?.userMdId || null
        };

        const metadata = {
          id: this.localMetadata?.id || undefined,
          name: this.saveDefnName.trim(),
          desc: this.saveDefnDesc.trim(),
          type: 'JobPackage'
        };

        const sessionState = window.vcCommunicator?.sessionState || { userMdId: null };
        const result = await this.savePackageCore(localPackage, metadata, sessionState, {
          onConfirmOverwrite: async (existingPackage, packageName) => {
            return new Promise((resolve) => {
              this.showConfirmOverwrite = true;
              this.existingPackageToOverwrite = existingPackage;
              this.overwriteResolve = resolve;
            });
          }
        });

        if (result.success) {
          this.localMetadata = result.metadata;
          this.persistMetadata();
          this.saveResultMessage = null;
          this.saveResultIsError = false;
          if (!this.pendingRunOnce) {
            // Save-only (no run) — close everything, show brief confirmation
            this.silentSaveMode = false;
            this.showSaveDefinitionModal = false;
            this.isSavingBulkTask = false;
            return;
          }
          // Start polling for completion
          this.startPollForCompletion();
        } else if (result.code !== 'USER_CANCELLED') {
          this.saveResultMessage = result.error || 'Failed to save definition.';
          this.saveResultIsError = true;
          this.silentSaveMode = false;
        }
      } catch (err) {
        console.error('Failed to save as bulk task:', err);
        this.saveResultMessage = 'An unexpected error occurred while saving.';
        this.saveResultIsError = true;
        this.silentSaveMode = false;
      } finally {
        this.isSavingBulkTask = false;
      }
    },

    // ============================================
    // Polling for bulk task completion
    // ============================================

    /** Start polling for bulk task results after submission */
    startPollForCompletion() {
      this.pollingState.pollingRetries = 0;
      this.pollingState.hasExceedRetries = false;
      this.pollingState.isRefreshingResults = true;
      this.delayedRefreshPackageResults();
    },

    /** Schedule the next polling attempt with a delay */
    delayedRefreshPackageResults(isError) {
      const delay = isError || this.pollingState.pollingRetries > 10 ? 10000 : 5000;
      this.pollingState.isRefreshingResults = true;
      if (this.pollingState.pollingTimer) {
        clearTimeout(this.pollingState.pollingTimer);
      }
      this.pollingState.pollingTimer = setTimeout(() => {
        if (this._isDestroyed) return;
        this.pollingState.pollingTimer = null;
        this.refreshPackageResults();
      }, delay);
    },

    /** Poll the server for task execution results */
    refreshPackageResults() {
      if (this.pollingState.hasExceedRetries) {
        console.error('Max polling retries reached');
        this.pollingState.isRefreshingResults = false;
        this.silentSaveMode = false;
        return;
      }
      this.pollingState.pollingRetries++;
      if (this.pollingState.pollingRetries > 30) {
        this.pollingState.hasExceedRetries = true;
        this.pollingState.isRefreshingResults = false;
        this.silentSaveMode = false;
        return;
      }
      if (this.pollingState.isPolling) return;
      if (!this.localMetadata || !this.localMetadata.id) return;

      this.pollingState.isPolling = true;
      window.vcCommunicator.resultsByJobPackage(this.localMetadata.id)
        .then((packageResults) => {
          if (packageResults && packageResults[0] && packageResults[0].instance) {
            this.taskInstance = packageResults[0].instance;
            if (packageResults[0].instance?.dateTime) this.lastExecutedDate = packageResults[0].instance.dateTime;
            if (packageResults[0].runonce) {
              this.taskInstance.status = VcJobStatus.QUEUED;
            }
          } else {
            this.taskInstance = null;
          }
          this.pollingState.isRefreshingResults =
            (!this.taskInstance || !VcJobStatus.isFinishedState(this.taskInstance.status));
          if (this.pollingState.isRefreshingResults) {
            this.delayedRefreshPackageResults();
          } else if (this.hasResult) {
            // Completed — auto-load results and close modal
            this.silentSaveMode = false;
            this.showSaveDefinitionModal = false;
            this.loadResultIntoDisplay().then(loaded => {
              if (!loaded) this.showEditor = true;
            });
          } else if (this.hasResultError) {
            // Finished with error — show modal so user sees the error
            this.silentSaveMode = false;
            this.showSaveDefinitionModal = true;
          }
        })
        .catch((error) => {
          console.log('Failed resultsByJobPackage:', error);
          this.pollingState.isRefreshingResults =
            this.pollingState.isRefreshingResults &&
            (!this.taskInstance || !VcJobStatus.isFinishedState(this.taskInstance.status));
          if (this.pollingState.isRefreshingResults) {
            this.delayedRefreshPackageResults(true);
          } else if (this.silentSaveMode) {
            this.silentSaveMode = false;
          }
        })
        .finally(() => {
          this.pollingState.isPolling = false;
        });
    },

    /** Download the completed bulk task results (CSV) — uses formatted endpoint for proper CSV rendering */
    downloadResult() {
      if (!window.vcCommunicator || !this.localMetadata) return;
      const request = window.vcCommunicator.constructFormattedDataRequest(this.localMetadata, true, 'csv');
      window.open(request);
    },

    /** Alias for downloadResult — explicit CSV download via formatted endpoint */
    downloadResultCsv() {
      this.downloadResult();
    },

    /** Download the completed bulk task results as a formatted XLSX spreadsheet */
    downloadResultXlsx() {
      if (!window.vcCommunicator || !this.localMetadata) return;
      const request = window.vcCommunicator.constructFormattedDataRequest(this.localMetadata, true, 'xlsx');
      window.open(request);
    },

    /** Download the completed bulk task results as a PDF report */
    downloadResultPdf() {
      if (!window.vcCommunicator || !this.localMetadata) return;
      const request = window.vcCommunicator.constructFormattedDataRequest(this.localMetadata, true, 'pdf');
      window.open(request);
    },

    /** Download the completed bulk task results as raw JSON */
    downloadResultJson() {
      if (!window.vcCommunicator || !this.localMetadata) return;
      const request = window.vcCommunicator.constructFormattedDataRequest(this.localMetadata, true, 'json');
      window.open(request);
    },

    /** Open the share manager dialog */
    async shareResultLink() {
      if (!this.localMetadata || !this.localMetadata.id) return;
      this.showShareManager = true;
    },

    /**
     * Load a shared view — called when ?share= URL param is present.
     * This is the public, read-only path that bypasses session/subscription checks.
     */
    async loadSharedViewData(password) {
      this.sharedViewLoading = true;
      this.sharedViewError = null;
      this.sharedViewPasswordRequired = false;
      this.sharedViewNoData = false;

      try {
        const serverRoot = this.serverRoot || window.vcCommunicator?.config?.serverroot || '/VisualCrossingWebServices/rest/services/';
        const data = await window.vcCommunicator.getSharedData(this.shareToken, password || this.sharedViewPassword, serverRoot);

        if (data.requiresPassword) {
          this.sharedViewPasswordRequired = true;
          this.sharedViewLoading = false;
          return;
        }

        if (data.noData || data.hasData === false) {
          this.sharedViewNoData = true;
          this.sharedViewName = data.shareName || data.share?.name || 'Shared View';
          this.sharedViewLoading = false;
          return;
        }

        // Store share metadata for display
        this.sharedViewName = data.shareName || data.share?.name || 'Shared View';

        // Restore unit group from shared data
        if (data.unitGroup) {
          this.setUnitGroup(data.unitGroup);
        }

        // Load criteria definitions if provided by server
        if (data.exceptionDefinition) {
          try {
            const defn = data.exceptionDefinition;
            const criteria = (defn.criteria || []).map(c => {
              let val = c.value;
              if (c.operator === 'between' && c.value2 != null && !isNaN(c.value2)) {
                val = [c.value, c.value2];
              }
              const criterion = createCriterion(
                c.element || c.field,
                c.operator,
                val,
                c.granularity || 'daily',
                c.name || null,
                { color: c.color || 'warning', isStub: !!c.isStub }
              );
              if (c.id) criterion.id = c.id;
              if (c.enabled === false) criterion.enabled = false;
              return criterion;
            });
            const combineMode = (defn.combinationMethod || 'or').toUpperCase();
            this.setCriteria(criteria, combineMode);

            if (defn.businessHours) {
              this.businessHours = {
                enabled: !!defn.businessHours.enabled,
                start: defn.businessHours.start || '08:00',
                end: defn.businessHours.end || '17:00'
              };
              this.setBusinessHours(this.businessHours);
            }
          } catch (e) {
            console.warn('Could not parse shared criteria:', e);
          }
        }

        // Map the result data into the display using the same logic as loadResultIntoDisplay
        const resultData = data.data;
        if (resultData && resultData.results && resultData.results.length) {
          const dictionaries = resultData.dictionaries || {};
          const isFlatJson = resultData.results[0]?.days && !Array.isArray(resultData.results[0].days);

          // Build name lookup from package locations (same pattern as loadResultIntoDisplay)
          const nameByLocation = {};
          if (data.locations && Array.isArray(data.locations)) {
            for (const loc of data.locations) {
              if (loc && loc.location) {
                nameByLocation[loc.location.toLowerCase().trim()] = loc.name || loc.location;
              }
            }
          }

          const mapped = resultData.results.map(loc => {
            let days;
            if (isFlatJson && loc.days && typeof loc.days === 'object') {
              days = this._expandFlatDays(loc.days, dictionaries);
            } else {
              days = loc.days || [];
            }

            const alerts = loc.alerts && loc.alerts.length ? loc.alerts : null;

            // Resolve user-assigned display name from package locations
            const serverAddr = (loc.address || loc.resolvedAddress || loc.name || '').toLowerCase().trim();
            const matchedName = nameByLocation[serverAddr] || null;
            const resolvedKey = (loc.resolvedAddress || '').toLowerCase().trim();
            const nameKey = (loc.name || '').toLowerCase().trim();
            const displayName = matchedName
              || (resolvedKey && nameByLocation[resolvedKey])
              || (nameKey && nameByLocation[nameKey])
              || loc.name || null;

            return {
              id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
              status: 'ok',
              errorMessage: null,
              cached: false,
              location: displayName || loc.address || loc.resolvedAddress,
              name: displayName,
              address: loc.address,
              resolvedAddress: loc.resolvedAddress,
              latitude: loc.latitude,
              longitude: loc.longitude,
              timezone: loc.timezone || null,
              exceptionCount: loc.exceptionCount || 0,
              maxSeverity: loc.maxSeverity || 'ok',
              warningCount: loc.warningCount || 0,
              dangerCount: loc.dangerCount || 0,
              nextExceptionDate: loc.nextExceptionDate || null,
              triggeredCriteria: loc.triggeredCriteria ? [...loc.triggeredCriteria] : [],
              alerts: alerts,
              alertsData: alerts,
              weatherData: {
                latitude: loc.latitude,
                longitude: loc.longitude,
                address: loc.resolvedAddress || loc.address || loc.name
              },
              resolvedLocation: {
                latitude: loc.latitude,
                longitude: loc.longitude,
                address: loc.resolvedAddress || loc.address || loc.name
              },
              days: days.map(day => {
                let activeAlerts = [];
                if (day.hasAlerts && alerts && alerts.length && day.datetime) {
                  const dayStart = new Date(day.datetime + 'T00:00:00');
                  const dayEnd = new Date(day.datetime + 'T23:59:59');
                  for (const alert of alerts) {
                    const onset = alert.onset ? new Date(alert.onset) : null;
                    const ends = alert.ends ? new Date(alert.ends) : null;
                    if ((!onset || onset <= dayEnd) && (!ends || ends >= dayStart)) {
                      activeAlerts.push(alert);
                    }
                  }
                }
                if (day.hourCriteriaMap) {
                  const normalized = {};
                  for (const [key, val] of Object.entries(day.hourCriteriaMap)) {
                    normalized[key.length === 5 ? key + ':00' : key] = val;
                  }
                  day.hourCriteriaMap = normalized;
                }
                if (day.matchedHours) {
                  day.matchedHours = day.matchedHours.map(h => h.length === 5 ? h + ':00' : h);
                }
                if (day.alertMatchedHours) {
                  day.alertMatchedHours = day.alertMatchedHours.map(h => h.length === 5 ? h + ':00' : h);
                }
                return { ...day, isExpanded: false, activeAlerts };
              })
            };
          });

          this.locationResults.splice(0, this.locationResults.length, ...mapped);
          this.hasRun = true;
        }
      } catch (err) {
        let errorMsg = 'Failed to load shared data';
        if (typeof err === 'string') {
          try {
            const parsed = JSON.parse(err);
            errorMsg = parsed.message || parsed.error || err;
          } catch (_e) {
            errorMsg = err;
          }
        } else if (err?.message) {
          errorMsg = err.message;
        }
        this.sharedViewError = errorMsg;
      } finally {
        this.sharedViewLoading = false;
      }
    },

    /** Submit password for a password-protected shared view */
    async submitSharedViewPassword() {
      if (!this.sharedViewPassword) return;
      await this.loadSharedViewData(this.sharedViewPassword);
    },

    /** Download XLSX from a shared view */
    downloadSharedXlsx() {
      this.downloadSharedFormat('xlsx');
    },

    /** Download CSV from a shared view */
    downloadSharedCsv() {
      this.downloadSharedFormat('csv');
    },

    /** Download a shared view in any supported format (pdf, json, etc.) */
    async downloadSharedFormat(format) {
      if (!this.shareToken) return;
      const serverRoot = this.serverRoot || window.vcCommunicator?.config?.serverroot || '/VisualCrossingWebServices/rest/services/';
      const url = serverRoot + 'share/' + encodeURIComponent(this.shareToken) + '/download?format=' + encodeURIComponent(format);

      if (!this.sharedViewPassword) {
        // No password — safe to open directly
        window.open(url);
        return;
      }

      // POST the password in the body so it doesn't appear in the URL
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'password=' + encodeURIComponent(this.sharedViewPassword)
        });
        if (!resp.ok) throw new Error('Download failed: ' + resp.status);
        const blob = await resp.blob();
        const ext = format === 'xlsx' ? 'xlsx' : format === 'pdf' ? 'pdf' : format === 'json' ? 'json' : 'csv';
        const filename = (this.sharedViewName || 'shared-view') + '.' + ext;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      } catch (e) {
        console.error('Shared download error:', e);
        alert('Download failed. Please try again.');
      }
    },

    /**
     * Fetch the backend JSON result and populate the frontend display.
     * The server-side WeatherExceptionAnalyzer produces a canonical result format
     * with all evaluation, severity, hasAlerts etc. already computed.
     * This method is a thin adapter that adds UI state (id, status, isExpanded)
     * and resolves flat JSON encoding.
     */
    async loadResultIntoDisplay() {
      if (!window.vcCommunicator || !this.localMetadata) {
        this.showEditor = true;
        return false;
      }
      this.isLoadingResult = true;
      try {
        const url = window.vcCommunicator.constructBulkWeatherDataRequest(this.localMetadata, true, false);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        if (!data || !data.results || !data.results.length) {
          console.warn('No results in backend response');
          this.showEditor = true;
          return false;
        }

        // Detect flatjson vs legacy json format
        const dictionaries = data.dictionaries || {};
        const isFlatJson = data.results[0]?.days && !Array.isArray(data.results[0].days);

        // Build a lookup map from locations to find user-assigned names
        const nameByLocation = {};
        for (const loc of this.locations) {
          if (typeof loc === 'string') {
            nameByLocation[loc.toLowerCase().trim()] = loc;
          } else if (loc && loc.location) {
            nameByLocation[loc.location.toLowerCase().trim()] = loc.name || loc.location;
          }
        }

        // Map server LocationExceptionResult → unified frontend result shape.
        // The server already computes: isException, severity, hasAlerts, matchedCriteria,
        // matchedHours, hourCriteriaMap, alertMatchedHours, and all weather context.
        // We only add UI state and resolve display names.
        const mapped = data.results.map(loc => {
          // Expand flat JSON days if needed
          let days;
          if (isFlatJson && loc.days && typeof loc.days === 'object') {
            days = this._expandFlatDays(loc.days, dictionaries);
          } else {
            days = loc.days || [];
          }

          // Resolve user-assigned display name from original locations
          const serverAddr = (loc.address || loc.resolvedAddress || loc.name || '').toLowerCase().trim();
          const matchedName = nameByLocation[serverAddr] || null;
          const resolvedKey = (loc.resolvedAddress || '').toLowerCase().trim();
          const nameKey = (loc.name || '').toLowerCase().trim();
          const displayName = matchedName
            || (resolvedKey && nameByLocation[resolvedKey])
            || (nameKey && nameByLocation[nameKey])
            || loc.name || null;

          const alerts = loc.alerts && loc.alerts.length ? loc.alerts : null;

          return {
            // UI state (client-only)
            id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
            status: 'ok',
            errorMessage: null,
            cached: false,
            // Location identity
            location: displayName || loc.address || loc.resolvedAddress,
            name: displayName,
            address: loc.address,
            resolvedAddress: loc.resolvedAddress,
            latitude: loc.latitude,
            longitude: loc.longitude,
            timezone: loc.timezone || null,
            // Exception summary (from server — no re-computation)
            exceptionCount: loc.exceptionCount || 0,
            maxSeverity: loc.maxSeverity || 'ok',
            warningCount: loc.warningCount || 0,
            dangerCount: loc.dangerCount || 0,
            nextExceptionDate: loc.nextExceptionDate || null,
            triggeredCriteria: loc.triggeredCriteria ? [...loc.triggeredCriteria] : [],
            // Alerts (from server)
            alerts: alerts,
            alertsData: alerts, // backward compat alias
            // Backward compat for ExceptionMapView
            weatherData: {
              latitude: loc.latitude,
              longitude: loc.longitude,
              address: loc.resolvedAddress || loc.address || loc.name
            },
            resolvedLocation: {
              latitude: loc.latitude,
              longitude: loc.longitude,
              address: loc.resolvedAddress || loc.address || loc.name
            },
            // Days — server provides isException, severity, hasAlerts, matchedCriteria etc.
            // We only add UI state (isExpanded) and resolve activeAlerts as full alert objects
            days: days.map(day => {
              // Resolve activeAlerts: match location-level alerts that overlap this day
              let activeAlerts = [];
              if (day.hasAlerts && alerts && alerts.length && day.datetime) {
                const dayStart = new Date(day.datetime + 'T00:00:00');
                const dayEnd = new Date(day.datetime + 'T23:59:59');
                for (const alert of alerts) {
                  const onset = alert.onset ? new Date(alert.onset) : null;
                  const ends = alert.ends ? new Date(alert.ends) : null;
                  if ((!onset || onset <= dayEnd) && (!ends || ends >= dayStart)) {
                    activeAlerts.push(alert);
                  }
                }
              }

              // Normalize hourCriteriaMap keys to HH:mm:ss format.
              // Server Java LocalTime.toString() produces "08:00" (omits seconds),
              // but client lookups construct "08:00:00". Align the two.
              if (day.hourCriteriaMap) {
                const normalized = {};
                for (const [key, val] of Object.entries(day.hourCriteriaMap)) {
                  normalized[key.length === 5 ? key + ':00' : key] = val;
                }
                day.hourCriteriaMap = normalized;
              }
              // Also normalize matchedHours and alertMatchedHours arrays
              if (day.matchedHours) {
                day.matchedHours = day.matchedHours.map(h => h.length === 5 ? h + ':00' : h);
              }
              if (day.alertMatchedHours) {
                day.alertMatchedHours = day.alertMatchedHours.map(h => h.length === 5 ? h + ':00' : h);
              }

              return {
                ...day,
                isExpanded: false,
                activeAlerts
              };
            })
          };
        });

        // Push results into the engine's reactive state
        this.locationResults.splice(0, this.locationResults.length, ...mapped);
        this.hasRun = true;
        this.captureRunFingerprint();
        this.showSaveDefinitionModal = false;

        // Apply persisted expand-all setting to the newly loaded results
        if (this.expandAllSetting && mapped.length) {
          this.expandedLocationIds = mapped.map(r => r.id);
        }
        return true;
      } catch (err) {
        console.error('Failed to load result into display:', err);
        this.showEditor = true;
        return false;
      } finally {
        this.isLoadingResult = false;
      }
    },

    /**
     * Convert flat/columnar days object into an array of day objects.
     * Handles dictionary lookups for conditions, icon, description.
     */
    _expandFlatDays(flatDays, dictionaries) {
      const dictFields = { conditions: 'conditions', icon: 'icon', description: 'description' };
      const keys = Object.keys(flatDays).filter(k => k !== 'hours');
      const count = flatDays.datetime ? flatDays.datetime.length : 0;
      const flatHours = flatDays.hours; // array of columnar hour objects per day (or undefined)
      const days = [];

      for (let i = 0; i < count; i++) {
        const day = {};
        for (const key of keys) {
          const arr = flatDays[key];
          if (!Array.isArray(arr)) continue;
          let val = arr[i];
          // Resolve dictionary indices
          if (val != null && dictFields[key] && dictionaries[key]) {
            val = dictionaries[key][val] ?? val;
          }
          day[key] = val;
        }
        // Expand hours for this day if present
        if (flatHours && flatHours[i] && typeof flatHours[i] === 'object') {
          day.hours = this._expandFlatHours(flatHours[i], dictionaries);
        }
        // Reconstruct hourCriteriaMap from per-hour matchedCriteria
        // (the flat JSON format serialises per-hour matchedCriteria but omits the day-level map)
        // hasAlerts is now provided directly by the server, so no client-side derivation needed.
        if (day.hours && day.hours.length) {
          const hourCriteriaMap = {};
          for (const hour of day.hours) {
            if (hour.matchedCriteria && hour.matchedCriteria.length) {
              hourCriteriaMap[hour.datetime] = hour.matchedCriteria;
            }
          }
          day.hourCriteriaMap = hourCriteriaMap;
        }
        days.push(day);
      }
      return days;
    },

    /**
     * Convert flat/columnar hours object into an array of hour objects.
     */
    _expandFlatHours(flatHours, dictionaries) {
      const dictFields = { conditions: 'conditions', icon: 'icon' };
      const keys = Object.keys(flatHours);
      const count = flatHours.datetime ? flatHours.datetime.length : 0;
      const hours = [];
      for (let i = 0; i < count; i++) {
        const hour = {};
        for (const key of keys) {
          const arr = flatHours[key];
          if (!Array.isArray(arr)) continue;
          let val = arr[i];
          if (val != null && dictFields[key] && dictionaries[key]) {
            val = dictionaries[key][val] ?? val;
          }
          hour[key] = val;
        }
        // Normalize hour.datetime to HH:mm:ss (server may send HH:mm)
        if (hour.datetime && hour.datetime.length === 5) {
          hour.datetime = hour.datetime + ':00';
        }
        hours.push(hour);
      }
      return hours;
    },

    /** Open the load definitions modal and fetch the filtered list */
    async openLoadDefinitions() {
      this.showBulkDefinitionsList = true;
      this.isLoadingDefinitions = true;
      this.savedDefinitions = [];
      this.pendingDeleteId = null;
      try {
        const results = await window.vcCommunicator.resultsByJobPackage(null, 'weatherexceptions');
        const allPackages = (results || []).filter(r => r?.jobPackage).map(r => {
          const pkg = r.jobPackage;
          if (r.instance) pkg.instance = r.instance;
          if (r.results) pkg.results = r.results;
          return pkg;
        });
        console.log('[Load] allPackages:', allPackages.length, 'first tags:', allPackages[0]?.tags);

        // Filter by goal tag (set during save). Falls back to loading definitions for packages without tags.
        const filtered = [];
        for (const pkg of allPackages) {
          // Check tag first (fast path)
          if (pkg.tags && Array.isArray(pkg.tags)) {
            if (pkg.tags.some(t => t === 'Goal: weatherexceptions')) {
              filtered.push(pkg);
              continue;
            }
            // Has tags but no matching goal tag — skip
            if (pkg.tags.length > 0) continue;
          }
          // No tags at all — fall back to loading the definition (for legacy packages saved before tagging)
          try {
            const defn = await window.vcCommunicator.loadBulkSourceDefn(pkg.id);
            if (defn?.params?.goal === 'weatherexceptions') {
              filtered.push(pkg);
            }
          } catch (e) {
            // skip packages whose definitions can't be loaded
          }
        }
        this.savedDefinitions = filtered;
      } catch (err) {
        console.error('Failed to load definitions:', err);
      } finally {
        this.isLoadingDefinitions = false;
      }
    },

    /** Delete a definition from the load list */
    async confirmDeleteDefinition(defn) {
      this.pendingDeleteId = null;
      try {
        await window.vcCommunicator.deleteMetadata(defn);
        this.savedDefinitions = this.savedDefinitions.filter(d => d.id !== defn.id);
        // If the deleted definition is currently loaded, clear it
        if (this.localMetadata && this.localMetadata.id === defn.id) {
          this.newDefinition();
        }
      } catch (err) {
        console.error('Failed to delete definition:', err);
        alert('Failed to delete definition: ' + (err.message || err));
      }
    },

    /** Select a definition from the lightweight load list */
    async selectSavedDefinition(metadata) {
      try {
        const taskDefn = await window.vcCommunicator.loadBulkSourceDefn(metadata.id);
        this.onLoadDefinition(metadata, taskDefn);
      } catch (err) {
        console.error('Failed to load definition:', err);
      }
    },

    /** Format date/time from epoch seconds, matching BulkDefinitionsList format */
    formatDateTime(epochSecs) {
      if (!epochSecs) return '';
      return VcDateUtils.formatDateTime2(epochSecs, null, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      });
    },

    /** Handle loaded definition (metadata, packageDefn) */
    onLoadDefinition(metadata, packageDefn) {
      try {
        const pkg = packageDefn || {};

        // 1. Restore locations
        const locations = pkg.locations || [];
        if (locations.length > 0) {
          this.setLocations(locations);
          this.persistLocations();
        }

        // 2. Restore unit group
        if (pkg.params?.unitGroup) {
          this.setUnitGroup(pkg.params.unitGroup);
        }

        // 3. Restore date period
        if (pkg.params?.startdate) {
          this.startPeriod = pkg.params.startdate;
        }

        // 4. Restore exception criteria
        if (pkg.params?.exceptiondefinition) {
          try {
            const defn = JSON.parse(pkg.params.exceptiondefinition);

            // Rebuild criteria using createCriterion, preserving original IDs
            // so they match matchedCriteria references in server results
            const criteria = (defn.criteria || []).map(c => {
              // Reconstruct 'between' value array from separate value/value2
              let val = c.value;
              if (c.operator === 'between' && c.value2 != null && !isNaN(c.value2)) {
                val = [c.value, c.value2];
              }
              const criterion = createCriterion(
                c.element || c.field,
                c.operator,
                val,
                c.granularity || 'daily',
                c.name || null,
                { color: c.color || 'warning', isStub: !!c.isStub }
              );
              // Preserve original ID so server result matchedCriteria arrays still align
              if (c.id) criterion.id = c.id;
              return criterion;
            });
            // Also restore enabled state
            criteria.forEach((cr, i) => {
              if (defn.criteria[i] && defn.criteria[i].enabled === false) cr.enabled = false;
            });

            const combineMode = (defn.combinationMethod || 'or').toUpperCase();
            this.setCriteria(criteria, combineMode);
            this.persistCriteria();

            // 5. Restore business hours
            if (defn.businessHours) {
              this.businessHours = {
                enabled: !!defn.businessHours.enabled,
                start: defn.businessHours.start || '08:00',
                end: defn.businessHours.end || '17:00'
              };
              this.setBusinessHours(this.businessHours);
              this.persistBusinessHours();
            }
          } catch (parseErr) {
            console.error('Failed to parse exception definition:', parseErr);
          }
        }

        // 6. Restore metadata linkage so re-submit updates the same package
        this.localMetadata = metadata || null;
        if (metadata?.name) {
          this.saveDefnName = metadata.name;
        }
        if (metadata?.desc) {
          this.saveDefnDesc = metadata.desc;
        }
        this.persistMetadata();

        // 6b. Restore schedule run times
        this.scheduleRunTimes = Array.isArray(pkg.runTimes) ? [...pkg.runTimes] : [];
        this.scheduleEnabled = pkg.enabled !== false;

        // 7. Set last executed date from instance if available
        if (metadata?.instance?.dateTime) {
          this.lastExecutedDate = metadata.instance.dateTime;
        }

        // 8. If the loaded definition has completed results, load them directly
        if (metadata?.instance && metadata.instance.status === 3) {
          this.taskInstance = metadata.instance;
          this.pollingState.isRefreshingResults = false;
          this.pollingState.isPolling = false;
          this.loadResultIntoDisplay().then(loaded => {
            if (!loaded) this.showEditor = true;
          });
        }

        this.showBulkDefinitionsList = false;
      } catch (err) {
        console.error('Failed to load definition:', err);
      }
    },

    /** Format a date for display (e.g., "Mon, Jan 15") */
    formatDisplayDate(datetime) {
      if (!datetime) return '';
      // If it's a date-only string (YYYY-MM-DD), append noon to avoid timezone shift
      const d = datetime.includes('T') ? new Date(datetime) : new Date(datetime + 'T12:00:00');
      if (isNaN(d.getTime())) return datetime; // fallback to raw string
      const opts = { weekday: 'short', month: 'short', day: 'numeric' };
      // Include time for full datetime values (e.g. alert onset/ends)
      if (datetime.includes('T')) {
        opts.hour = 'numeric';
        opts.minute = '2-digit';
      }
      return d.toLocaleDateString('en-US', opts);
    },

    /** Format alert description text — replace * with line breaks */
    formatAlertDescription(desc) {
      if (!desc) return '';
      return desc.replace(/(\*)/g, '<br>');
    }
  }
};
</script>

<style scoped>
/* Loading screen shown during initialization */
.initializing-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}
.initializing-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.flexible-weather-tracker {
  --fwt-bg: #ffffff;
  --fwt-bg-secondary: #f8fafc;
  --fwt-bg-card: #ffffff;
  --fwt-text: #1e293b;
  --fwt-text-muted: #586674;
  --fwt-border: #c5ced6;
  --fwt-border-light: #dce3ea;
  --fwt-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  --fwt-shadow-lg: 0 4px 16px rgba(0, 0, 0, 0.12);
  --fwt-radius: 12px;
  --fwt-radius-sm: 8px;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--fwt-text);
  background: var(--fwt-bg);
  padding: 1rem;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Criteria editor invalid card */
.criterion-editor-card--invalid {
  border: 2px solid var(--bs-danger) !important;
  background-color: rgba(220, 53, 69, 0.06);
  animation: shake-criterion 0.4s ease;
}
@keyframes shake-criterion {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}

/* Left column: scrollable cards + sticky button */
.setup-column {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
}

.setup-scroll-area {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding-right: 4px;
}

.setup-run-button {
  flex: 0 0 auto;
  padding-top: 0.5rem;
  position: sticky;
  bottom: 0;
  background: var(--fwt-bg);
  z-index: 2;
}

/* Compact card styling */
.setup-scroll-area .card-body {
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
}

.tracker-header {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--fwt-border);
  margin-bottom: 1rem;
}

.header-icon {
  width: 42px;
  height: 42px;
  border-radius: var(--fwt-radius-sm);
  background: linear-gradient(135deg, #6366f1, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);
}

.empty-state {
  background: var(--fwt-bg-secondary);
  border-radius: var(--fwt-radius);
  border: 2px dashed var(--fwt-border);
}

.results-view {
  background: var(--fwt-bg-card);
  border-radius: var(--fwt-radius);
  border: 1px solid var(--fwt-border);
  box-shadow: var(--fwt-shadow);
  overflow: hidden;
}

/* Hourly forecast strip */
.hourly-strip {
  overflow: visible;
}

.hourly-grid {
  display: flex;
  overflow-x: auto;
  padding: 4px;
  gap: 1px;
}

.hourly-cell {
  flex: 0 0 auto;
  width: 48px;
  text-align: center;
  padding: 4px 2px;
  border-radius: 6px;
  background: var(--fwt-bg-secondary);
  font-size: 0.72rem;
  transition: background 0.15s, box-shadow 0.15s;
  cursor: default;
}

.hourly-cell:hover {
  background: var(--fwt-border-light);
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}



.hourly-mini-gantt {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  margin-top: 2px;
}
.hourly-gantt-bar {
  width: 100%;
  height: 4px;
}
.hourly-gantt-bar.mini-gantt-bar-danger  { background: #dc3545; }
.hourly-gantt-bar.mini-gantt-bar-warning { background: #ffc107; }
.hourly-gantt-bar.mini-gantt-bar-info    { background: #3c76f2; }
.hourly-gantt-bar.mini-gantt-bar-purple  { background: #6f42c1; }
.hourly-gantt-bar.mini-gantt-bar-orange  { background: #fd7e14; }
.hourly-gantt-bar.mini-gantt-bar-teal    { background: #20c997; }
.hourly-gantt-bar.mini-gantt-bar-pink    { background: #d63384; }

.hourly-cell.hourly-outside-business {
  opacity: 0.4;
}

.hourly-time {
  font-weight: 600;
  color: var(--fwt-text-muted);
  font-size: 0.65rem;
}

.hourly-temp {
  font-weight: 700;
  font-size: 0.8rem;
}

.hourly-detail {
  font-size: 0.6rem;
  min-height: 14px;
}

.hourly-expansion td {
  background: var(--bs-light);
}

/* Custom badge color overrides */
:deep(.bg-purple) {
  background-color: #6f42c1 !important;
  color: #fff;
}
:deep(.bg-orange) {
  background-color: #fd7e14 !important;
  color: #fff;
}
:deep(.bg-pink) {
  background-color: #d63384 !important;
  color: #fff;
}
:deep(.bg-teal) {
  background-color: #20c997 !important;
  color: #fff;
}
:deep(.bg-indigo) {
  background-color: #6610f2 !important;
  color: #fff;
}
:deep(.bg-cyan) {
  background-color: #0891b2 !important;
  color: #fff;
}
:deep(.bg-lime) {
  background-color: #84cc16 !important;
  color: #000;
}

/* Custom text colors for outline badges */
.text-purple { color: #6f42c1 !important; }
.text-orange { color: #fd7e14 !important; }
.text-pink { color: #d63384 !important; }
.text-teal { color: #20c997 !important; }
.text-indigo { color: #6610f2 !important; }
.text-cyan { color: #0891b2 !important; }
.text-lime { color: #84cc16 !important; }

/* Custom border colors for outline badges */
.border-purple { border-color: #6f42c1 !important; }
.border-orange { border-color: #fd7e14 !important; }
.border-pink { border-color: #d63384 !important; }
.border-teal { border-color: #20c997 !important; }
.border-indigo { border-color: #6610f2 !important; }
.border-cyan { border-color: #0891b2 !important; }
.border-lime { border-color: #84cc16 !important; }

/* ===== Site Detail Modal ===== */
.site-detail-dialog {
  margin: 1.5rem auto;
  height: calc(100vh - 3rem);
  max-height: calc(100vh - 3rem);
  max-width: min(90vw, 1400px);
}
.site-detail-dialog .modal-content {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid var(--fwt-border);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
}
.site-detail-dialog .modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

/* Narrow screens: nearly full width */
@media (max-width: 992px) {
  .site-detail-dialog {
    margin: 0.5rem;
    height: calc(100vh - 1rem);
    max-height: calc(100vh - 1rem);
    max-width: calc(100vw - 1rem);
  }
}

/* ===== Site Detail Modal Tabs ===== */
.modal-tab-nav {
  background: var(--fwt-bg-secondary);
  flex-shrink: 0;
}
.modal-tab-nav .nav-tabs {
  border-bottom: none;
}
.modal-tab-nav .nav-link {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--fwt-text-muted);
  border: none;
  border-bottom: 2px solid transparent;
  padding: 8px 16px;
  transition: color 0.15s, border-color 0.15s;
}
.modal-tab-nav .nav-link:hover {
  color: var(--bs-body-color);
  border-bottom-color: var(--bs-border-color);
}
.modal-tab-nav .nav-link.active {
  color: var(--bs-primary);
  border-bottom-color: var(--bs-primary);
  background: transparent;
}

/* ===== Daily Gantt Chart ===== */
.daily-gantt-criterion-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* ===== Weather Alert Cards ===== */
.site-alerts-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.site-alert-card {
  border: 1px solid #e8b4b8;
  border-radius: var(--fwt-radius-sm);
  overflow: hidden;
  background: var(--fwt-bg-card);
  box-shadow: var(--fwt-shadow);
}
.site-alert-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #fff0f0, #fff8f8);
  cursor: pointer;
  user-select: none;
}
.site-alert-header:hover {
  background: linear-gradient(135deg, #ffe8e8, #fff0f0);
}
.site-alert-icon {
  color: var(--bs-danger);
  font-size: 1rem;
  flex-shrink: 0;
}
.site-alert-event {
  font-weight: 600;
  font-size: 0.85rem;
  color: #842029;
}
.site-alert-time {
  font-size: 0.75rem;
  color: var(--bs-secondary);
  white-space: nowrap;
}
.site-alert-chevron {
  font-size: 0.7rem;
  color: var(--bs-secondary);
  flex-shrink: 0;
}
.site-alert-body {
  padding: 0.5rem 0.75rem 0.6rem;
  border-top: 1px solid #f0e0e0;
  background: var(--bs-body-bg);
}
.site-alert-headline {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--bs-body-color);
  margin-bottom: 0.35rem;
  line-height: 1.35;
}
.site-alert-desc {
  font-size: 0.78rem;
  color: var(--bs-secondary);
  max-height: 120px;
  overflow-y: auto;
  line-height: 1.45;
}
.site-alert-link {
  display: inline-block;
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: #dc3545;
  text-decoration: none;
}
.site-alert-link:hover {
  text-decoration: underline;
}

/* ===== Hourly Gantt Chart ===== */
/* ===== Horizontal Hourly Gantt ===== */
.hgantt {
  border: 1px solid var(--fwt-border);
  border-radius: var(--fwt-radius-sm);
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: var(--fwt-shadow);
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}
.hgantt-criterion-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #d5d5d5;
  min-width: fit-content;
}
.hgantt-criterion-row:last-child {
  border-bottom: none;
}
.hgantt-label {
  flex: 0 0 130px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--fwt-text-muted);
  background: var(--fwt-bg-secondary);
  border-right: 1px solid var(--fwt-border-light);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.hgantt-cells {
  flex: 1;
  display: flex;
  min-width: 0;
}
.hgantt-cell {
  flex: 1 1 0;
  min-width: 32px;
  height: 18px;
  background: #f5f5f5;
  border-right: 1px solid rgba(255,255,255,0.6);
  transition: opacity 0.15s;
}
.hgantt-cell:last-child {
  border-right: none;
}
.hgantt-cell--flagged {
  opacity: 1; /* background set inline */
}
.hgantt-cell--outside {
  opacity: 0.3;
}
.hgantt-cell--alert {
  box-shadow: inset 0 -2px 0 rgba(220,53,69,0.4);
}
/* Daily cells are a bit taller to accommodate text */
.hgantt-cell--daily {
  height: 22px;
}
.hgantt-cells--clickable .hgantt-cell {
  cursor: pointer;
}
.hgantt-cells--clickable .hgantt-cell:hover {
  opacity: 0.8;
}
.hgantt-cell--selected {
  border-left: 2px solid var(--bs-primary);
  border-right: 2px solid var(--bs-primary);
}
.hgantt-criterion-row:first-child .hgantt-cell--selected {
  border-top: 2px solid var(--bs-primary);
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
}
.hgantt-criterion-row:nth-last-child(2) .hgantt-cell--selected {
  border-bottom: 2px solid var(--bs-primary);
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
}
.hgantt-day-label {
  cursor: pointer;
  flex-direction: column;
  line-height: 1;
  height: auto;
  padding: 4px 0 2px 0;
}
.hgantt-day-label--selected {
  color: var(--bs-primary) !important;
  font-weight: 700;
}
.hgantt-day-name {
  font-size: 0.6rem;
  font-weight: 600;
}
.hgantt-day-num {
  font-size: 0.55rem;
  color: var(--bs-secondary);
}
/* Hour labels along bottom */
.hgantt-hour-labels {
  background: var(--fwt-bg-secondary);
  border-top: 1px solid var(--fwt-border-light);
}
.hgantt-hour-label {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.58rem;
  font-weight: 600;
  color: var(--bs-secondary);
  height: 16px;
  background: transparent;
}

/* ===== Gantt Cell Text Labels ===== */
.hgantt-cell--with-value {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.hgantt-cell-text {
  font-size: 0.55rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hgantt-cell-text--flagged {
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}
.hgantt-cell-text--normal {
  color: var(--bs-secondary);
}
/* Warning/orange bars need dark text */
.hgantt-cell--flagged[style*="rgb(255, 193, 7)"] .hgantt-cell-text--flagged,
.hgantt-cell--flagged[style*="#ffc107"] .hgantt-cell-text--flagged {
  color: #000;
  text-shadow: none;
}
.hgantt-cell--flagged[style*="rgb(253, 126, 20)"] .hgantt-cell-text--flagged,
.hgantt-cell--flagged[style*="#fd7e14"] .hgantt-cell-text--flagged {
  color: #000;
  text-shadow: none;
}
.hgantt-cell--flagged[style*="rgb(60, 118, 242)"] .hgantt-cell-text--flagged,
.hgantt-cell--flagged[style*="#3c76f2"] .hgantt-cell-text--flagged {
  color: #fff;
}
.hgantt-cell--flagged[style*="rgb(32, 201, 151)"] .hgantt-cell-text--flagged,
.hgantt-cell--flagged[style*="#20c997"] .hgantt-cell-text--flagged {
  color: #000;
  text-shadow: none;
}

/* ===== Day Detail Panel (popup) ===== */
.day-detail-panel {
  background: var(--fwt-bg-secondary);
  border: 1px solid var(--fwt-border);
  border-radius: var(--fwt-radius);
  padding: 14px 18px;
  box-shadow: var(--fwt-shadow);
}
.day-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--bs-border-color);
}
.day-detail-date {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}
.day-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
}
.day-detail-stat {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  background: var(--fwt-bg-card);
  border-radius: var(--fwt-radius-sm);
  border: 1px solid var(--fwt-border);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s;
}
.day-detail-stat:hover {
  box-shadow: var(--fwt-shadow);
}
.day-detail-stat-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
  margin-top: 2px;
}
.day-detail-stat-info {
  min-width: 0;
}
.day-detail-stat-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: var(--fwt-text-muted);
  letter-spacing: 0.05em;
  font-weight: 600;
}
.day-detail-stat-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--fwt-text);
  line-height: 1.3;
}
.day-detail-stat-sub {
  font-size: 0.72rem;
  color: var(--fwt-text-muted);
}
.day-detail-sparklines {
  border-top: 1px solid var(--bs-border-color);
  padding-top: 8px;
}
.sparkline-container {
  background: var(--fwt-bg-card);
  border-radius: var(--fwt-radius-sm);
  border: 1px solid var(--fwt-border);
  padding: 8px;
}
.sparkline-canvas {
  width: 100%;
  height: 180px;
}

/* ===== Forecast Tab ===== */
.site-forecast {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.forecast-day-card {
  border: 1px solid var(--fwt-border);
  border-radius: var(--fwt-radius-sm);
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.15s;
}
.forecast-day-card:hover {
  box-shadow: var(--fwt-shadow);
  transform: translateY(-1px);
}
.forecast-day-card--expanded {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 1px rgba(13,110,253,0.15);
}
.forecast-day-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.1s;
}
.forecast-day-header:hover {
  background: var(--bs-light);
}
.forecast-day-date {
  flex: 0 0 50px;
  text-align: center;
}
.forecast-day-name {
  font-weight: 700;
  font-size: 0.82rem;
  color: var(--bs-body-color);
}
.forecast-day-num {
  font-size: 0.7rem;
  color: var(--bs-secondary);
}
.forecast-day-icon {
  flex: 0 0 32px;
}
.forecast-wx-icon {
  width: 32px;
  height: 32px;
}
.forecast-wx-icon-sm {
  width: 22px;
  height: 22px;
}
.forecast-day-temps {
  flex: 0 0 70px;
  display: flex;
  gap: 6px;
  align-items: baseline;
}
.forecast-hi {
  font-weight: 700;
  font-size: 1rem;
}
.forecast-lo {
  font-size: 0.85rem;
}
.forecast-day-precip {
  flex: 0 0 60px;
  font-size: 0.82rem;
}
.forecast-day-wind {
  flex: 0 0 80px;
  font-size: 0.82rem;
  color: var(--bs-body-color);
}
.forecast-day-chevron {
  flex: 0 0 20px;
  text-align: center;
  color: var(--bs-gray-500);
  margin-left: auto;
}
.forecast-hourly-panel {
  border-top: 1px solid var(--bs-secondary-bg);
  background: #fcfcfd;
}
.forecast-hourly-grid {
  display: flex;
  flex-direction: column;
}
.forecast-hour-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  border-bottom: 1px solid #d5d5d5;
  font-size: 0.8rem;
}
.forecast-hour-row:last-child {
  border-bottom: none;
}
.forecast-hour-row--outside {
  opacity: 0.45;
}
.forecast-hour-time {
  flex: 0 0 42px;
  font-weight: 600;
  color: var(--bs-secondary);
  font-size: 0.75rem;
}
.forecast-hour-icon {
  flex: 0 0 24px;
}
.forecast-hour-temp {
  flex: 0 0 36px;
}
.forecast-hour-precip {
  flex: 0 0 56px;
  font-size: 0.78rem;
}
.forecast-hour-humidity {
  flex: 0 0 50px;
  font-size: 0.78rem;
}
.forecast-hour-wind {
  flex: 0 0 110px;
  font-size: 0.78rem;
  color: var(--bs-body-color);
}
.forecast-hour-cond {
  flex: 1;
  min-width: 0;
}

/* ===== Site Map Tab ===== */
.site-map-container {
  border-radius: var(--fwt-radius);
  overflow: hidden;
  border: 1px solid var(--fwt-border);
  box-shadow: var(--fwt-shadow);
}

/* ===== Alert Range Highlighting ===== */
.row-alert-active {
  background: #fff5f5 !important;
  border-left: 3px solid var(--bs-danger);
}

.row-alert-active td:first-child {
  padding-left: 5px;
}

.row-alert-active:hover {
  background: #fee2e2 !important;
}

.hourly-alert-active {
  background: #fee2e2 !important;
  box-shadow: inset 0 -2px 0 var(--bs-danger);
}

/* ===== Alert Accordion ===== */
.alert-accordion {
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  overflow: hidden;
}

.alert-accordion-item {
  border-bottom: 1px solid #f5c6cb;
}

.alert-accordion-item:last-child {
  border-bottom: none;
}

.alert-accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  background: #fff5f5;
  transition: background 0.15s ease;
  gap: 8px;
}

.alert-accordion-header:hover {
  background: #fee2e2;
}

.alert-accordion-open .alert-accordion-header {
  background: #fee2e2;
  border-bottom: 1px solid #f5c6cb;
}

.alert-accordion-chevron {
  font-size: 0.7rem;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  color: var(--bs-secondary);
}

.alert-accordion-chevron.rotated {
  transform: rotate(90deg);
}

.alert-accordion-body {
  padding: 10px 12px 10px 32px;
  background: var(--bs-body-bg);
  border-top: none;
}

/* Fixed-size criteria editor modal */
.criteria-editor-dialog {
  margin: 2rem auto;
  height: calc(100vh - 4rem);
  max-height: calc(100vh - 4rem);
}
.criteria-editor-dialog .modal-content {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}
.criteria-editor-dialog .modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}
.criteria-editor-dialog .modal-footer {
  flex-shrink: 0;
}
/* Hide the editor's own footer actions when inside the modal (they're in modal-footer instead) */
.criteria-editor-dialog :deep(.ece-footer-actions) {
  display: none;
}

/* Single scroll container — provides both scrollbars at the 500px viewport edge */
.grid-scroll-container {
  max-height: 500px;
  overflow: auto;
}

/* Disable child grid's own scroll wrapper so content flows into our container.
   :deep() is required because <style scoped> won't reach child component elements. */
.grid-scroll-container :deep(.grid-scroll-wrapper) {
  overflow: visible !important;
  max-height: none !important;
}

/* ===== View Panel ===== */
.view-panel {
  border: 1px solid var(--fwt-border);
  border-radius: var(--fwt-radius);
  background: var(--fwt-bg-card);
  box-shadow: var(--fwt-shadow);
  overflow: hidden;
  transition: opacity 0.25s ease;
}
.view-panel--stale {
  opacity: 0.55;
  pointer-events: auto;
  position: relative;
}

/* ===== Editor & View Toolbars ===== */
.editor-toolbar {
  padding: 8px 10px;
  background: var(--fwt-bg-secondary);
  border: 1px solid var(--fwt-border-light);
  border-radius: var(--fwt-radius-sm);
}
.view-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Position dropdown menus below button (no Bootstrap JS to do this) */
.btn-group > .dropdown-menu {
  top: 100%;
  right: 0;
  left: auto;
  margin-top: 2px;
  box-shadow: 0 4px 12px rgba(0,0,0,.12);
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,.1);
  padding: 6px 0;
}

/* ===== Stale Data Banner ===== */
.stale-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  margin-bottom: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: var(--fwt-radius-sm);
  color: #92400e;
  font-size: 0.82rem;
  font-weight: 500;
}
.stale-banner i { font-size: 1rem; }

.view-toggle-bar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 10px 12px;
  background: var(--fwt-bg-secondary);
  border-bottom: 1px solid var(--fwt-border-light);
}
.view-toggle-bar > .btn-group {
  grid-column: 2;
  justify-self: center;
}
.view-toggle-bar > .filter-card-search {
  grid-column: 3;
  justify-self: end;
}

.view-panel-footer {
  padding: 8px 12px;
  background: var(--fwt-bg-secondary);
  border-top: 1px solid var(--fwt-border-light);
}

/* ===== Unified Filter Cards (KPI style) ===== */
.filter-cards-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.filter-cards-kpis {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
}

.filter-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border: 1px solid var(--fwt-border);
  border-top: 3px solid var(--bs-primary);
  border-radius: var(--fwt-radius);
  background: rgba(59, 130, 246, 0.04);
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
  min-width: 64px;
  text-align: center;
  box-shadow: var(--fwt-shadow);
}

.filter-card:hover {
  box-shadow: var(--fwt-shadow-lg);
  transform: translateY(-1px);
}

.filter-card-active {
  border-color: var(--bs-primary) !important;
  border-top-color: var(--bs-primary) !important;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25), var(--fwt-shadow-lg) !important;
  background: #eff6ff !important;
}

.filter-card-count {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--bs-primary);
}

.filter-card-label {
  font-size: 0.65rem;
  color: var(--fwt-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  text-align: center;
  line-height: 1.3;
  margin-top: 2px;
}

/* Built-in color variants (All / No Risk / Any Risk) */
.filter-card-ok { border-top-color: var(--bs-success); background: rgba(25, 135, 84, 0.04); }
.filter-card-ok .filter-card-count { color: var(--bs-success); }
.filter-card-risk { border-top-color: var(--bs-danger); background: rgba(220, 53, 69, 0.04); }
.filter-card-risk .filter-card-count { color: var(--bs-danger); }

/* Criterion cards — colored via inline :style (borderTopColor, backgroundColor, count color) */
.filter-card-criterion {
  border-top-width: 3px;
  border-top-style: solid;
}

/* Fallback class-based color variants (used by criteriaCardClass) */
.filter-card-danger { border-top-color: #dc3545; background: rgba(220, 53, 69, 0.05); }
.filter-card-danger .filter-card-count { color: #dc3545; }
.filter-card-warning { border-top-color: #ffc107; background: rgba(255, 193, 7, 0.06); }
.filter-card-warning .filter-card-count { color: #856404; }
.filter-card-info { border-top-color: #3c76f2; background: rgba(60, 118, 242, 0.05); }
.filter-card-info .filter-card-count { color: #3c76f2; }
.filter-card-success { border-top-color: #198754; background: rgba(25, 135, 84, 0.05); }
.filter-card-success .filter-card-count { color: #198754; }
.filter-card-purple { border-top-color: #6f42c1; background: rgba(111, 66, 193, 0.05); }
.filter-card-purple .filter-card-count { color: #6f42c1; }
.filter-card-orange { border-top-color: #fd7e14; background: rgba(253, 126, 20, 0.05); }
.filter-card-orange .filter-card-count { color: #fd7e14; }
.filter-card-pink { border-top-color: #d63384; background: rgba(214, 51, 132, 0.05); }
.filter-card-pink .filter-card-count { color: #d63384; }
.filter-card-teal { border-top-color: #20c997; background: rgba(32, 201, 151, 0.05); }
.filter-card-teal .filter-card-count { color: #0d9488; }
.filter-card-indigo { border-top-color: #6610f2; background: rgba(102, 16, 242, 0.05); }
.filter-card-indigo .filter-card-count { color: #6610f2; }
.filter-card-cyan { border-top-color: #0891b2; background: rgba(8, 145, 178, 0.05); }
.filter-card-cyan .filter-card-count { color: #0891b2; }
.filter-card-lime { border-top-color: #84cc16; background: rgba(132, 204, 22, 0.06); }
.filter-card-lime .filter-card-count { color: #65a30d; }

.filter-card-search {
  flex-direction: row;
  gap: 4px;
  padding: 4px 10px;
  min-width: 130px;
  border-top-width: 1px;
  align-self: center;
  height: auto;
}
.filter-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8rem;
  width: 100%;
  min-width: 0;
  font-family: inherit;
}

/* ===== Global Refinements (cards, badges, tables) ===== */
.flexible-weather-tracker :deep(.card) {
  border-radius: var(--fwt-radius);
  border-color: var(--fwt-border);
  box-shadow: var(--fwt-shadow);
  overflow: hidden;
}

.flexible-weather-tracker :deep(.card-header) {
  background: var(--fwt-bg-secondary);
  border-bottom-color: var(--fwt-border);
  font-weight: 600;
  font-size: 0.85rem;
}

.flexible-weather-tracker :deep(.badge) {
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
}

.flexible-weather-tracker :deep(.table) {
  font-size: 0.82rem;
}

.flexible-weather-tracker :deep(.table th) {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: var(--fwt-text-muted);
}

.flexible-weather-tracker :deep(.nav-tabs .nav-link) {
  font-size: 0.85rem;
  font-weight: 500;
}

.flexible-weather-tracker :deep(.btn) {
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* ===== Responsive ===== */
@media (max-width: 767.98px) {
  .flexible-weather-tracker {
    padding: 0.5rem;
    font-size: 13px;
  }
  .filter-card-count {
    font-size: 1.2rem;
  }
  .header-icon {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }
}
</style>
